// Purpose: render the Callable Console dashboard using Figma screens plus live API hydration.
import {
  Activity,
  CalendarDays,
  Check,
  ChevronDown,
  CreditCard,
  KeyRound,
  LayoutDashboard,
  LogOut,
  Mic2,
  Phone,
  Plus,
  Search,
  Settings,
  Webhook,
  Wrench,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { CallableMark } from "./CallableMark";
import { callbackMessage, clearCallbackParameters } from "./authCallback";
import { AddNumberDialog } from "./AddNumberDialog";
import type { AddNumberValues } from "./AddNumberDialog";
import { RecordingPolicyPanel } from "./RecordingPolicyPanel";
import { SipTrunksScreen } from "./SipTrunksScreen";
import { EmptyState, MetricCard, StatusPill } from "./uiPrimitives";
import type {
  ApiAdmissionPolicy,
  ApiAgent,
  ApiBillingReconciliationHistory,
  ApiConsoleTool,
  ApiCredential,
  ApiDashboardInvitation,
  ApiDashboardMember,
  ApiNumber,
  ApiRuntimeFlag,
  ApiSipTrunk,
  ApiTtsVoice,
  ApiWebhookDelivery,
  ApiWebhookEndpoint,
  CallReviewData,
  ContactInteraction,
  ContactMemory,
  ContactMemoryPacket,
  CurrentTenantMembership,
  CurrentUser,
  InternalCallCost,
  OAuthLoginStart,
  OpenItemChanges,
} from "./apiClient";
import {
  acceptDashboardInvitation,
  archiveAgent,
  archiveAgentVersion,
  assignNumberToAgent,
  assignNumberToTrunk,
  ConsoleApiError,
  consoleEnvironmentLabel,
  createBillingReconciliation,
  createContactInteraction,
  createDashboardInvitation,
  createTool,
  createToolRevision,
  createWebhookEndpoint,
  enqueueLowBalanceEvent,
  fetchAdmissionPolicy,
  fetchAgents,
  fetchBillingReconciliations,
  fetchCallMemoryImpact,
  fetchCallRecordingPlaybackUrl,
  fetchCallRecordings,
  fetchCallSummary,
  fetchCallTranscripts,
  fetchContactInteractions,
  fetchContactMemory,
  fetchCredentials,
  fetchCurrentUser,
  fetchDashboardData,
  fetchDashboardInvitations,
  fetchDashboardMembers,
  fetchInternalCallCost,
  fetchRuntimeFlags,
  fetchSipTrunks,
  fetchTools,
  fetchTtsVoices,
  fetchWebhookDeliveries,
  fetchWebhookEndpoints,
  isUnauthorizedApiError,
  provisionSubaccount,
  logoutCurrentUser,
  registerNumber,
  revokeCredential,
  revokeDashboardMember,
  startOAuthLogin,
  setConsoleTenantSelection,
  syncTtsVoices,
  updateAdmissionPolicy,
  updateDashboardMemberRole,
} from "./apiClient";
import { AgentBuilderScreen } from "./AgentBuilder";
import type {
  BillingSummaryRow,
  CallRow,
  CallTimelineEvent,
  ConsoleNumber,
  RuntimeStatusRow,
} from "./mockData";

const emptyRuntimeStatus: RuntimeStatusRow = {
  livekitSipUri: "Not connected",
  sipDispatch: "Unavailable until live API data loads",
  inboundVoiceProven: false,
  productionRuntimeDeployed: false,
};

const emptyBillingSummary: BillingSummaryRow = {
  balance: "—",
  available: "—",
  held: "—",
  lowBalanceThreshold: "—",
  currency: "AUD",
  isLowBalance: false,
  updatedAt: "Unavailable",
  recentLedgerEntries: [],
  recentCallCosts: [],
};

type Screen =
  | "Overview"
  | "Agents"
  | "Voices"
  | "Tools & MCP"
  | "Numbers"
  | "SIP Trunks"
  | "Calls"
  | "Contact Memory"
  | "Credentials"
  | "Webhooks"
  | "Usage & billing"
  | "Safety"
  | "Runtime Flags"
  | "Settings";

const SELECTED_WORKSPACE_STORAGE_KEY = "callable_console.selected_workspace.v1";

function workspaceKey(value: Pick<CurrentTenantMembership, "account_id" | "subaccount_id">): string {
  return `${value.account_id}:${value.subaccount_id}`;
}

function workspaceLabel(membership: CurrentTenantMembership | null): string {
  return membership?.subaccount_name ?? membership?.account_name ?? "CallableAI";
}

function selectInitialWorkspace(user: CurrentUser): CurrentTenantMembership | null {
  if (user.memberships.length === 0) return null;
  const stored = window.localStorage.getItem(SELECTED_WORKSPACE_STORAGE_KEY);
  const selected = user.memberships.find((membership) => workspaceKey(membership) === stored);
  return selected ?? user.memberships[0];
}

const navItems: { label: Screen; icon: typeof LayoutDashboard }[] = [
  { label: "Overview", icon: LayoutDashboard },
  { label: "Agents", icon: CalendarDays },
  { label: "Voices", icon: Mic2 },
  { label: "Tools & MCP", icon: Wrench },
  { label: "Numbers", icon: Phone },
  { label: "SIP Trunks", icon: Phone },
  { label: "Calls", icon: Activity },
  { label: "Contact Memory", icon: Search },
  { label: "Credentials", icon: KeyRound },
  { label: "Webhooks", icon: Webhook },
  { label: "Usage & billing", icon: CreditCard },
  { label: "Safety", icon: Activity },
  { label: "Runtime Flags", icon: Settings },
  { label: "Settings", icon: Settings },
];

export function App() {
  const [authState, setAuthState] = useState<
    "bootstrapping" | "authenticated" | "unauthenticated"
  >("bootstrapping");
  const [currentUser, setCurrentUser] = useState<CurrentUser | null>(null);
  const [selectedWorkspace, setSelectedWorkspace] = useState<CurrentTenantMembership | null>(null);
  const [tenantRefreshNonce, setTenantRefreshNonce] = useState(0);
  const [callbackFailure] = useState(() => callbackMessage(window.location.search));
  const [authMessage, setAuthMessage] = useState<string | null>(null);
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [activeScreen, setActiveScreen] = useState<Screen>("Overview");
  const [numbers, setNumbers] = useState<ConsoleNumber[]>([]);
  const [calls, setCalls] = useState<CallRow[]>([]);
  const [runtimeStatus, setRuntimeStatus] =
    useState<RuntimeStatusRow>(emptyRuntimeStatus);
  const [billingSummary, setBillingSummary] =
    useState<BillingSummaryRow>(emptyBillingSummary);
  const [apiState, setApiState] = useState<
    "idle" | "loading" | "live" | "unavailable"
  >("idle");
  const [apiError, setApiError] = useState<string | null>(null);

  const expireSession = useCallback(
    (message = "Your session expired. Please sign in again.") => {
      setCurrentUser(null);
      setSelectedWorkspace(null);
      setConsoleTenantSelection(null);
      setAuthState("unauthenticated");
      setAuthMessage(message);
      setSessionError(null);
      setApiState("idle");
      setIsLoggingOut(false);
    },
    [],
  );

  useEffect(() => {
    let cancelled = false;
    clearCallbackParameters();
    fetchCurrentUser()
      .then((user) => {
        if (cancelled) return;
        if (!isValidCurrentUser(user)) {
          setCurrentUser(null);
          setAuthState("unauthenticated");
          setAuthMessage(callbackFailure ?? "No active session. Please sign in again.");
          return;
        }
        const workspace = selectInitialWorkspace(user);
        setCurrentUser(user);
        setSelectedWorkspace(workspace);
        setConsoleTenantSelection(
          workspace
            ? { account_id: workspace.account_id, subaccount_id: workspace.subaccount_id }
            : null,
        );
        setAuthState("authenticated");
        setAuthMessage(null);
        setSessionError(null);
        setApiState("loading");
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setCurrentUser(null);
        setAuthState("unauthenticated");
        setAuthMessage(
          callbackFailure ?? (isUnauthorizedApiError(error)
            ? null
            : "Console could not check your session. Please try again or contact your administrator."),
        );
      });
    return () => {
      cancelled = true;
    };
  }, [callbackFailure]);

  useEffect(() => {
    if (authState !== "authenticated" || !selectedWorkspace) return;
    setConsoleTenantSelection({
      account_id: selectedWorkspace.account_id,
      subaccount_id: selectedWorkspace.subaccount_id,
    });
    window.localStorage.setItem(SELECTED_WORKSPACE_STORAGE_KEY, workspaceKey(selectedWorkspace));
    let cancelled = false;
    fetchDashboardData()
      .then((data) => {
        if (cancelled) return;
        setNumbers(data.numbers);
        setCalls(data.calls.filter((call) => call.source !== "seed_demo"));
        setRuntimeStatus(data.runtimeStatus);
        setBillingSummary(data.billingSummary);
        setApiState("live");
        setApiError(null);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (isUnauthorizedApiError(error)) {
          expireSession();
          return;
        }
        setApiState("unavailable");
        setApiError(
          error instanceof ConsoleApiError
            ? error.message
            : "Console API request failed",
        );
      });
    return () => {
      cancelled = true;
    };
  }, [authState, selectedWorkspace, tenantRefreshNonce, expireSession]);

  function clearTenantScopedDashboardState() {
    setNumbers([]);
    setCalls([]);
    setRuntimeStatus(emptyRuntimeStatus);
    setBillingSummary(emptyBillingSummary);
    setApiState("loading");
    setApiError(null);
  }

  function handleWorkspaceChange(membership: CurrentTenantMembership) {
    if (selectedWorkspace && workspaceKey(selectedWorkspace) === workspaceKey(membership)) return;
    clearTenantScopedDashboardState();
    setSelectedWorkspace(membership);
  }

  async function handleWorkspaceCreated(input: {
    name: string;
    slug: string;
    environment: "local" | "staging" | "production";
  }) {
    const created = await provisionSubaccount(input);
    const refreshed = await fetchCurrentUser();
    if (!isValidCurrentUser(refreshed)) {
      expireSession("Workspace was created, but your session no longer has tenant context.");
      return;
    }
    const nextWorkspace = refreshed.memberships.find(
      (membership) =>
        membership.account_id === created.account_id &&
        membership.subaccount_id === created.subaccount_id,
    );
    if (!nextWorkspace) {
      throw new ConsoleApiError(
        "Workspace was created, but the current session was not granted membership. Refresh and try again.",
      );
    }
    clearTenantScopedDashboardState();
    setCurrentUser(refreshed);
    setSelectedWorkspace(nextWorkspace);
    setTenantRefreshNonce((current) => current + 1);
  }

  async function handleLogout() {
    if (isLoggingOut) return;
    setSessionError(null);
    setIsLoggingOut(true);
    try {
      await logoutCurrentUser();
      expireSession("Signed out. Choose a provider to continue.");
    } catch {
      setSessionError("Logout failed. Your session may still be active. Please try again.");
      setIsLoggingOut(false);
    }
  }

  if (authState === "bootstrapping") {
    return <AuthBootstrapScreen />;
  }

  if (authState === "unauthenticated") {
    return <LoginScreen message={authMessage} />;
  }

  return (
    <div className="console-shell" data-theme="dark">
      <Sidebar
        activeScreen={activeScreen}
        onSelect={setActiveScreen}
        billingSummary={billingSummary}
        user={currentUser}
        selectedWorkspace={selectedWorkspace}
        onWorkspaceChange={handleWorkspaceChange}
        onWorkspaceCreated={handleWorkspaceCreated}
      />
      <main className="workspace">
        <Topbar
          current={activeScreen}
          user={currentUser}
          onLogout={() => void handleLogout()}
          isLoggingOut={isLoggingOut}
          apiState={apiState}
        />
        <div className="content-stack">
          {apiState === "unavailable" || sessionError ? (
            <div className="api-banner warning" role="alert">
              {sessionError ??
                `${consoleEnvironmentLabel()} Console data could not be loaded. ${apiError ?? ""}`}
            </div>
          ) : null}
          {activeScreen === "Overview" ? (
            <OverviewScreen
              calls={calls}
              numbers={numbers}
              runtimeStatus={runtimeStatus}
            />
          ) : null}
          {activeScreen === "Voices" ? (
            <VoicesScreen onSessionExpired={expireSession} />
          ) : null}
          {activeScreen === "Tools & MCP" ? (
            <ToolsMcpScreen
              onSessionExpired={expireSession}
              onOpenAgents={() => setActiveScreen("Agents")}
            />
          ) : null}
          {activeScreen === "Numbers" ? (
            <NumbersScreen
              numbers={numbers}
              onNumbersChange={setNumbers}
              onSessionExpired={expireSession}
            />
          ) : null}
          {activeScreen === "SIP Trunks" ? (
            <SipTrunksScreen onSessionExpired={expireSession} />
          ) : null}
          {activeScreen === "Calls" ? (
            <CallsScreen
              calls={calls}
              runtimeStatus={runtimeStatus}
              onSessionExpired={expireSession}
            />
          ) : null}
          {activeScreen === "Contact Memory" ? (
            <ContactMemoryScreen onSessionExpired={expireSession} />
          ) : null}
          {activeScreen === "Credentials" ? (
            <CredentialsScreen onSessionExpired={expireSession} />
          ) : null}
          {activeScreen === "Agents" ? (
            <AgentBuilderScreen onSessionExpired={expireSession} />
          ) : null}
          {activeScreen === "Webhooks" ? (
            <WebhooksScreen onSessionExpired={expireSession} />
          ) : null}
          {activeScreen === "Usage & billing" ? (
            <BillingScreen
              billingSummary={billingSummary}
              onSessionExpired={expireSession}
            />
          ) : null}
          {activeScreen === "Safety" ? (
            <SafetyControlsScreen onSessionExpired={expireSession} />
          ) : null}
          {activeScreen === "Runtime Flags" ? (
            <RuntimeFlagsScreen onSessionExpired={expireSession} />
          ) : null}
          {activeScreen === "Settings" ? (
            <SettingsScreen
              currentUser={currentUser}
              onSessionExpired={expireSession}
            />
          ) : null}
        </div>
      </main>
    </div>
  );
}

function isValidCurrentUser(user: CurrentUser): boolean {
  return user.authenticated === true && user.user_id.trim().length > 0;
}

function AuthBootstrapScreen() {
  return (
    <main
      className="auth-bootstrap-page"
      data-theme="dark"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="auth-bootstrap-card">
        <AuthBrand />
        <div className="auth-heading-stack">
          <h1>Restoring your Console session…</h1>
          <p>
            Checking the secure session cookie before any dashboard data loads.
          </p>
        </div>
        <div className="auth-bootstrap-progress" aria-hidden="true">
          <span />
        </div>
      </div>
    </main>
  );
}

function LoginScreen(props: { message: string | null }) {
  const [loginState, setLoginState] = useState<string | null>(null);

  async function handleProvider(provider: OAuthLoginStart["provider"]) {
    setLoginState(`Opening ${provider} sign-in…`);
    try {
      const response = await startOAuthLogin(provider);
      window.location.assign(response.authorization_url);
    } catch {
      setLoginState("Could not start sign-in. Please try again or contact your administrator.");
    }
  }

  return (
    <main className="auth-page" data-theme="dark">
      <section className="auth-form-side" aria-labelledby="signin-heading">
        <div className="auth-form-stack">
          <AuthBrand />
          <div className="auth-heading-stack">
            <h1 id="signin-heading">Sign in</h1>
            <p>Welcome back.</p>
          </div>
          <div className="auth-social-stack">
            <button
              className="provider-button"
              type="button"
              onClick={() => void handleProvider("google")}
            >
              <span
                className="provider-icon provider-google"
                aria-hidden="true"
              >
                G
              </span>
              <span>Sign in with Google</span>
            </button>
            <button
              className="provider-button"
              type="button"
              onClick={() => void handleProvider("github")}
            >
              <span
                className="provider-icon provider-github"
                aria-hidden="true"
              >
                ◖
              </span>
              <span>Sign in with GitHub</span>
            </button>
          </div>
          <div className="auth-divider">
            <span>or</span>
          </div>
          <div
            className="auth-field-stack"
            aria-label="Email and password sign-in placeholder"
          >
            <label className="auth-field">
              <span>Work email</span>
              <input value="you@company.com" disabled aria-disabled="true" />
            </label>
            <label className="auth-field">
              <span>Password</span>
              <input value="••••••••••••" disabled aria-disabled="true" />
            </label>
          </div>
          <button
            className="auth-primary-button"
            type="button"
            disabled
            title="Email/password sign-in is not enabled for this staging console."
          >
            Sign in
          </button>
          <p className="auth-create-copy">
            New to Callable? <span>Create an account</span>
          </p>
          {props.message ? (
            <div className="api-banner warning">{props.message}</div>
          ) : null}
          {loginState ? (
            <p className="compact-copy" role="status">
              {loginState}
            </p>
          ) : null}
        </div>
      </section>
      <AuthPitch />
    </main>
  );
}

function AuthBrand() {
  return (
    <div className="auth-brand">
      <span className="brand-mark brand-mark-lg">
        <CallableMark size={22} />
      </span>
      <span>Callable Console</span>
    </div>
  );
}

function AuthPitch() {
  return (
    <aside className="auth-pitch" aria-label="Callable Console product summary">
      <div className="auth-pitch-stack">
        <h2>
          Australian phone numbers for your agents. Your models, your keys.
        </h2>
        <pre>
          {
            'curl -X POST https://api.callable.com/v1/numbers \\n  -H "Authorization: Bearer $CALLABLE_KEY" \\n  -d \'{ "type": "landline", "agent": "agt_8fa2" }\'\n\n→ +61 2 8103 4471  ringing in 8 seconds'
          }
        </pre>
        <div className="auth-proof-list">
          <AuthProof
            title="Telephony included, not passed through"
            detail="Vapi and Retell charge a platform fee on top of your Twilio bill. Our number is the number."
          />
          <AuthProof
            title="Bring your own LLM, STT and TTS"
            detail="Your keys, your provider invoices. We never fall back to ours."
          />
          <AuthProof
            title="Talk to your agent before you buy a number"
            detail="The browser test call uses the same room a real call does."
          />
        </div>
      </div>
    </aside>
  );
}

function AuthProof(props: { title: string; detail: string }) {
  return (
    <div className="auth-proof">
      <span aria-hidden="true">✓</span>
      <div>
        <strong>{props.title}</strong>
        <p>{props.detail}</p>
      </div>
    </div>
  );
}

function Sidebar(props: {
  activeScreen: Screen;
  onSelect: (screen: Screen) => void;
  billingSummary: BillingSummaryRow;
  user: CurrentUser | null;
  selectedWorkspace: CurrentTenantMembership | null;
  onWorkspaceChange: (membership: CurrentTenantMembership) => void;
  onWorkspaceCreated: (input: { name: string; slug: string; environment: "local" | "staging" | "production" }) => Promise<void>;
}) {
  const [isWorkspaceMenuOpen, setIsWorkspaceMenuOpen] = useState(false);
  return (
    <aside className="sidebar" aria-label="Console navigation">
      <div className="logo-row">
        <span className="brand-mark">
          <CallableMark size={17} title="Callable" />
        </span>
        <span className="logo-text">Console</span>
      </div>
      <div className="tenant-switcher-wrap">
        <button
          className="tenant-switcher"
          type="button"
          aria-expanded={isWorkspaceMenuOpen}
          onClick={() => setIsWorkspaceMenuOpen((current) => !current)}
          title="Switch Console workspace"
        >
          <span>
            <strong>{workspaceLabel(props.selectedWorkspace)}</strong>
            <small>
              {props.selectedWorkspace
                ? `${props.selectedWorkspace.account_name ?? "Account"} · ${props.selectedWorkspace.role}`
                : formatIdentity(
                    props.user?.email ??
                      props.user?.user_id ??
                      "Authenticated session",
                  )}
            </small>
          </span>
          <ChevronDown size={16} aria-hidden="true" />
        </button>
        {isWorkspaceMenuOpen ? (
          <WorkspaceSwitcherMenu
            memberships={props.user?.memberships ?? []}
            selectedWorkspace={props.selectedWorkspace}
            onSelect={(membership) => {
              props.onWorkspaceChange(membership);
              setIsWorkspaceMenuOpen(false);
            }}
            onCreate={async (input) => {
              await props.onWorkspaceCreated(input);
              setIsWorkspaceMenuOpen(false);
            }}
          />
        ) : null}
      </div>
      <nav className="nav-list">
        {navItems.map((item) => {
          const Icon = item.icon;
          return (
            <button
              className={`nav-item ${item.label === props.activeScreen ? "active" : ""}`}
              type="button"
              onClick={() => props.onSelect(item.label)}
              key={item.label}
            >
              <Icon size={16} strokeWidth={1.9} aria-hidden="true" />
              <span>{item.label}</span>
            </button>
          );
        })}
      </nav>
      <div
        className={`balance-card ${props.billingSummary.isLowBalance ? "warning-balance" : ""}`}
      >
        <div className="table-label">Balance</div>
        <strong>{props.billingSummary.available}</strong>
        <span>
          {props.billingSummary.held} held · {props.billingSummary.currency}
        </span>
      </div>
    </aside>
  );
}

function WorkspaceSwitcherMenu(props: {
  memberships: CurrentTenantMembership[];
  selectedWorkspace: CurrentTenantMembership | null;
  onSelect: (membership: CurrentTenantMembership) => void;
  onCreate: (input: { name: string; slug: string; environment: "local" | "staging" | "production" }) => Promise<void>;
}) {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [environment, setEnvironment] = useState<"local" | "staging" | "production">("production");
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canCreate = name.trim().length > 0 && /^[a-z0-9][a-z0-9-]*[a-z0-9]$/.test(slug.trim());

  function handleNameChange(value: string) {
    setName(value);
    if (!slug.trim()) {
      setSlug(slugFromWorkspaceName(value));
    }
  }

  async function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canCreate || isCreating) return;
    setIsCreating(true);
    setError(null);
    try {
      await props.onCreate({
        name: name.trim(),
        slug: slug.trim(),
        environment,
      });
    } catch (createError) {
      setError(
        createError instanceof ConsoleApiError
          ? createError.message
          : "Workspace could not be created.",
      );
      setIsCreating(false);
    }
  }

  return (
    <div className="workspace-menu" role="menu" aria-label="Console workspaces">
      <div className="workspace-menu-section">
        <span className="table-label">Switch workspace</span>
        {props.memberships.length === 0 ? (
          <p className="compact-copy">No workspace memberships are available.</p>
        ) : (
          props.memberships.map((membership) => {
            const selected = props.selectedWorkspace
              ? workspaceKey(props.selectedWorkspace) === workspaceKey(membership)
              : false;
            return (
              <button
                key={workspaceKey(membership)}
                className={`workspace-option ${selected ? "active" : ""}`}
                type="button"
                role="menuitem"
                onClick={() => props.onSelect(membership)}
              >
                <span>
                  <strong>{workspaceLabel(membership)}</strong>
                  <small>{membership.account_name ?? "Account"} · {membership.role}</small>
                </span>
                {selected ? <Check size={15} aria-hidden="true" /> : null}
              </button>
            );
          })
        )}
      </div>
      <form className="workspace-create-form" onSubmit={handleCreate}>
        <span className="table-label">Create workspace</span>
        <label>
          <span>Name</span>
          <input
            value={name}
            onChange={(event) => handleNameChange(event.target.value)}
            placeholder="Customer workspace"
            maxLength={120}
          />
        </label>
        <label>
          <span>Slug</span>
          <input
            value={slug}
            onChange={(event) => setSlug(slugFromWorkspaceName(event.target.value))}
            placeholder="customer-workspace"
            maxLength={80}
          />
        </label>
        <label>
          <span>Environment</span>
          <select
            value={environment}
            onChange={(event) => setEnvironment(event.target.value as "local" | "staging" | "production")}
          >
            <option value="production">Production</option>
            <option value="staging">Staging</option>
            <option value="local">Local</option>
          </select>
        </label>
        {error ? <p className="form-error" role="alert">{error}</p> : null}
        <button className="button-primary" type="submit" disabled={!canCreate || isCreating}>
          {isCreating ? "Creating…" : "Create workspace"}
        </button>
      </form>
    </div>
  );
}

function slugFromWorkspaceName(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

function Topbar(props: {
  current: Screen;
  user: CurrentUser | null;
  onLogout: () => void;
  isLoggingOut: boolean;
  apiState: "idle" | "loading" | "live" | "unavailable";
}) {
  const identity = formatIdentity(
    props.user?.email ?? props.user?.user_id ?? "Authenticated session",
  );
  const environment = consoleEnvironmentLabel();
  // Environment and connection live here rather than in two stacked banners, so
  // the signal stays permanently visible without costing space on every screen.
  const connection =
    props.apiState === "live"
      ? "Connected"
      : props.apiState === "unavailable"
        ? "Needs attention"
        : "Connecting";
  return (
    <header className="topbar">
      <div className="breadcrumb">
        <span>CallableAI</span>
        <span aria-hidden="true">/</span>
        <strong>{props.current}</strong>
      </div>
      <span
        className={`env-chip env-${environment.toLowerCase().replace(/\s+/g, "-")}`}
        title={`${environment} environment · ${connection}`}
      >
        <i aria-hidden="true" />
        {environment}
        <span className="env-sep" aria-hidden="true">
          ·
        </span>
        <span className="env-state">{connection}</span>
      </span>
      <div className="topbar-actions">
        <label className="search-box">
          <Search size={16} aria-hidden="true" />
          <input placeholder="Search numbers" aria-label="Search numbers" />
        </label>
        <div className="session-chip" aria-label="Signed-in session context">
          <span>Signed in as</span>
          <strong>{identity}</strong>
        </div>
        <button
          className="logout-button"
          type="button"
          onClick={props.onLogout}
          disabled={props.isLoggingOut}
        >
          <LogOut size={15} aria-hidden="true" />
          {props.isLoggingOut ? "Logging out…" : "Logout"}
        </button>
      </div>
    </header>
  );
}

function formatIdentity(value: string): string {
  if (value.includes("@")) return value;
  if (value.length <= 14) return value;
  return `${value.slice(0, 8)}…${value.slice(-4)}`;
}

function OverviewScreen(props: {
  calls: CallRow[];
  numbers: ConsoleNumber[];
  runtimeStatus: RuntimeStatusRow;
}) {
  const overviewCalls = props.calls.slice(0, 5);
  const realCalls = useMemo(
    () => props.calls.filter((call) => call.source === "real_uat"),
    [props.calls],
  );
  const answered = useMemo(
    () =>
      realCalls.filter((call) => call.endReason !== "never_answered").length,
    [realCalls],
  );
  const testNumber =
    props.numbers.find((number) => number.uatRole === "staging_test_did") ??
    props.numbers.find((number) => number.did === "+61 3907 21700");
  return (
    <section className="screen" aria-labelledby="overview-heading">
      <header className="screen-header compact">
        <div>
          <h1 id="overview-heading">Overview</h1>
          <p>CallableAI internal tenant · last 7 days · production data</p>
        </div>
      </header>
      <RealModeWarning />
      {testNumber ? (
        <TestNumberPanel number={testNumber} latestRealCall={realCalls[0]} />
      ) : null}
      <div className="metric-grid">
        <MetricCard
          label="Calls answered"
          value={answered ? answered.toLocaleString() : "—"}
          detail={realCalls.length ? "live API rows" : "waiting for live data"}
          tone={answered ? "good" : undefined}
        />
        <MetricCard
          label="Avg call length"
          value="—"
          detail="computed after usage API loads"
        />
        <MetricCard
          label="Median response"
          value="—"
          detail="computed after telemetry loads"
        />
        <MetricCard
          label="Spend"
          value="—"
          detail="usage-only/manual billing"
        />
      </div>
      <div className="overview-grid">
        <section
          className="panel recent-calls"
          aria-labelledby="recent-calls-heading"
        >
          <h2 id="recent-calls-heading">Recent calls</h2>
          <div className="activity-list">
            {overviewCalls.length ? (
              overviewCalls.map((call) => (
                <div className="activity-row" key={call.id}>
                  <span className="muted mono">
                    {call.started.replace("20 Aug ", "")}
                  </span>
                  <span className="mono">{call.caller}</span>
                  <span>{call.agent}</span>
                  <span className="mono">{call.duration}</span>
                  <span>Real UAT</span>
                  <span
                    className={
                      call.endReason === "never_answered" ? "muted" : "success"
                    }
                  >
                    {call.endReason === "never_answered"
                      ? "no answer"
                      : "completed"}
                  </span>
                </div>
              ))
            ) : (
              <EmptyState message="No live call rows loaded yet." />
            )}
          </div>
        </section>
        <aside className="side-stack" aria-label="Operational status">
          <section className="panel attention-panel">
            <h2>Live checks</h2>
            <EmptyState message="No blocking operational alerts returned by the API." />
          </section>
          <section className="panel quickstart-panel">
            <h2>Quick start</h2>
            <ChecklistItem done label="Create an agent" />
            <ChecklistItem done label="Add your provider keys" />
            <ChecklistItem done label="Buy a number" />
            <ChecklistItem label="Send us a webhook endpoint" />
            <ChecklistItem label="Make your first API call" />
          </section>
        </aside>
      </div>
    </section>
  );
}

function ChecklistItem(props: { label: string; done?: boolean }) {
  return (
    <div className="check-row">
      <span className={`check-box ${props.done ? "done" : ""}`}>
        {props.done ? <Check size={12} aria-hidden="true" /> : null}
      </span>
      <span>{props.label}</span>
    </div>
  );
}

function memoryListItems(values: unknown[]): string[] {
  return values.filter((value): value is string => typeof value === 'string' && value.trim().length > 0);
}

function MemoryStateLists(props: { memory: ContactMemory | null }) {
  const preferences = memoryListItems(props.memory?.known_preferences ?? []);
  const openItems = memoryListItems(props.memory?.open_items ?? []);
  return (
    <div className="summary-text">
      <h3>Known preferences</h3>
      {preferences.length ? <ul>{preferences.map((item) => <li key={item}>{item}</li>)}</ul> : <p>No known preferences stored.</p>}
      <h3>Open items</h3>
      {openItems.length ? <ul>{openItems.map((item) => <li key={item}>{item}</li>)}</ul> : <p>No open items stored.</p>}
    </div>
  );
}

function hasOpenItemChanges(changes: OpenItemChanges | null): changes is OpenItemChanges {
  return Boolean(changes && (
    changes.opened_items.length > 0
    || changes.legacy_items_added.length > 0
    || changes.resolved_items.length > 0
    || changes.ignored_resolutions.length > 0
    || changes.dropped_items.length > 0
  ));
}

function OpenItemChangesPanel(props: { changes: OpenItemChanges | null }) {
  if (!hasOpenItemChanges(props.changes)) {
    return <p className="compact-copy">No open-item changes recorded for this call.</p>;
  }
  const { changes } = props;
  return (
    <div className="summary-text">
      <h3>Open item changes</h3>
      {changes.opened_items.length > 0 && <><strong>Opened</strong><ul>{changes.opened_items.map((item) => <li key={item}>{item}</li>)}</ul></>}
      {changes.legacy_items_added.length > 0 && <><strong>Opened from legacy payload</strong><ul>{changes.legacy_items_added.map((item) => <li key={item}>{item}</li>)}</ul></>}
      {changes.resolved_items.length > 0 && <><strong>Resolved</strong><ul>{changes.resolved_items.map((item, index) => <li key={`${item.item ?? item.item_text ?? index}-${index}`}>{item.item ?? item.item_text ?? 'Unknown item'}{item.reason ? ` — ${item.reason}` : ''}</li>)}</ul></>}
      {changes.ignored_resolutions.length > 0 && <><strong>Ignored resolutions</strong><ul>{changes.ignored_resolutions.map((item, index) => <li key={`${item.item_text ?? index}-${index}`}>{item.item_text ?? `Item #${item.item_number ?? '?'}`} — {item.ignored_reason}{item.reason ? ` (${item.reason})` : ''}</li>)}</ul></>}
      {changes.dropped_items.length > 0 && <><strong>Dropped by cap</strong><ul>{changes.dropped_items.map((item, index) => <li key={`${item.item ?? index}-${index}`}>{item.item ?? 'Unknown item'} — {item.reason}</li>)}</ul></>}
    </div>
  );
}

function ContactMemoryScreen(props: { onSessionExpired: () => void }) {
  const [contactId, setContactId] = useState("");
  const [summary, setSummary] = useState("");
  const [nextAction, setNextAction] = useState("");
  const [agentVisible, setAgentVisible] = useState(false);
  const [internalOnly, setInternalOnly] = useState(false);
  const [memory, setMemory] = useState<ContactMemoryPacket | null>(null);
  const [interactions, setInteractions] = useState<ContactInteraction[]>([]);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  async function loadMemory(targetContactId = contactId) {
    const trimmedContactId = targetContactId.trim();
    if (!trimmedContactId) return;
    setIsBusy(true);
    setStatusMessage(null);
    try {
      const [memoryPacket, recentInteractions] = await Promise.all([
        fetchContactMemory(trimmedContactId),
        fetchContactInteractions(trimmedContactId),
      ]);
      setMemory(memoryPacket);
      setInteractions(recentInteractions);
      setStatusMessage("Contact memory loaded.");
    } catch (error) {
      if (isUnauthorizedApiError(error)) {
        props.onSessionExpired();
        return;
      }
      setStatusMessage(
        error instanceof ConsoleApiError
          ? error.message
          : "Could not load contact memory.",
      );
    } finally {
      setIsBusy(false);
    }
  }

  async function submitInteraction(event: FormEvent) {
    event.preventDefault();
    const trimmedContactId = contactId.trim();
    if (!trimmedContactId || !summary.trim()) return;
    setIsBusy(true);
    setStatusMessage(null);
    try {
      await createContactInteraction({
        contact_id: trimmedContactId,
        interaction_type: "manual_note",
        occurred_at: new Date().toISOString(),
        direction: "manual",
        summary: summary.trim(),
        next_action: nextAction.trim() || null,
        agent_visible: agentVisible,
        internal_only: internalOnly,
        visibility_reason: agentVisible
          ? "Operator marked this note as safe for agent context."
          : null,
        metadata: { source: "dashboard_contact_memory_screen" },
      });
      setSummary("");
      setNextAction("");
      setStatusMessage("Interaction saved and memory updated.");
      await loadMemory(trimmedContactId);
    } catch (error) {
      if (isUnauthorizedApiError(error)) {
        props.onSessionExpired();
        return;
      }
      setStatusMessage(
        error instanceof ConsoleApiError
          ? error.message
          : "Could not save contact interaction.",
      );
    } finally {
      setIsBusy(false);
    }
  }

  return (
    <section className="screen" aria-labelledby="contact-memory-heading">
      <header className="screen-header">
        <div>
          <h1 id="contact-memory-heading">Contact Memory</h1>
          <p>
            View durable contact context and add operator-approved notes for
            future AI calls.
          </p>
          {statusMessage ? (
            <p className="compact-copy" role="status">
              {statusMessage}
            </p>
          ) : null}
        </div>
      </header>
      <div className="billing-grid">
        <form
          className="table-card"
          onSubmit={(event) => void submitInteraction(event)}
        >
          <div className="panel-title-row">
            <h2>Add interaction</h2>
            <span className="table-label">Manual note</span>
          </div>
          <label className="auth-field">
            <span>Contact ID</span>
            <input
              required
              value={contactId}
              onChange={(event) => setContactId(event.target.value)}
              placeholder="contact-123"
            />
          </label>
          <label>
            Summary
            <textarea
              required
              value={summary}
              onChange={(event) => setSummary(event.target.value)}
              placeholder="What should Console remember?"
              rows={4}
            />
          </label>
          <label className="auth-field">
            <span>Next action</span>
            <input
              value={nextAction}
              onChange={(event) => setNextAction(event.target.value)}
              placeholder="Optional follow-up for staff or agent"
            />
          </label>
          <label className="checkbox-field">
            <input
              type="checkbox"
              checked={agentVisible}
              onChange={(event) => setAgentVisible(event.target.checked)}
              disabled={internalOnly}
            />{" "}
            Agent visible
          </label>
          <label className="checkbox-field">
            <input
              type="checkbox"
              checked={internalOnly}
              onChange={(event) => setInternalOnly(event.target.checked)}
              disabled={agentVisible}
            />{" "}
            Internal only
          </label>
          <button
            className="primary-button"
            type="submit"
            disabled={isBusy || !contactId.trim() || !summary.trim()}
          >
            <Plus size={15} aria-hidden="true" />
            Save note
          </button>
        </form>
        <div className="table-card">
          <div className="panel-title-row">
            <h2>Memory lookup</h2>
            <span className="table-label">Tenant scoped</span>
          </div>
          <label className="auth-field">
            <span>Contact ID</span>
            <input
              value={contactId}
              onChange={(event) => setContactId(event.target.value)}
              placeholder="contact-123"
            />
          </label>
          <button
            className="secondary-button"
            type="button"
            onClick={() => void loadMemory()}
            disabled={isBusy || !contactId.trim()}
          >
            <Search size={15} aria-hidden="true" />
            Load memory
          </button>
          <div className="notice compact-notice">
            <strong>Agent-safe summary</strong>
            <p>
              {memory?.contact_memory?.agent_visible_summary ||
                "No agent-visible memory loaded yet."}
            </p>
          </div>
          <MemoryStateLists memory={memory?.contact_memory ?? null} />
          <div className="notice compact-notice">
            <strong>Last interaction</strong>
            <p>
              {memory?.contact_memory?.last_interaction_at
                ? new Date(
                    memory.contact_memory.last_interaction_at,
                  ).toLocaleString("en-AU")
                : "—"}
            </p>
          </div>
        </div>
      </div>
      <div className="table-card">
        <div className="panel-title-row">
          <h2>Recent interactions</h2>
          <span className="table-label">{interactions.length} loaded</span>
        </div>
        <table>
          <thead>
            <tr>
              <th>When</th>
              <th>Type</th>
              <th>Summary</th>
              <th>Agent</th>
              <th>Internal</th>
            </tr>
          </thead>
          <tbody>
            {interactions.length === 0 ? (
              <tr>
                <td colSpan={5}>No interactions loaded.</td>
              </tr>
            ) : (
              interactions.map((interaction) => (
                <tr key={interaction.interaction_id}>
                  <td>
                    {new Date(interaction.occurred_at).toLocaleString("en-AU")}
                  </td>
                  <td>{interaction.interaction_type}</td>
                  <td>{interaction.summary}</td>
                  <td>{interaction.agent_visible ? "visible" : "hidden"}</td>
                  <td>{interaction.internal_only ? "yes" : "no"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function NumbersScreen(props: {
  numbers: ConsoleNumber[];
  onNumbersChange: (numbers: ConsoleNumber[]) => void;
  onSessionExpired: () => void;
}) {
  const [addOpen, setAddOpen] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [addBusy, setAddBusy] = useState(false);
  const [agents, setAgents] = useState<ApiAgent[]>([]);
  const [trunks, setTrunks] = useState<ApiSipTrunk[]>([]);
  const [assignmentState, setAssignmentState] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([fetchAgents(), fetchSipTrunks()])
      .then(([loadedAgents, loadedTrunks]) => {
        if (!cancelled) {
          setAgents(loadedAgents);
          setTrunks(loadedTrunks);
        }
      })
      .catch((error) => {
        if (isUnauthorizedApiError(error)) props.onSessionExpired();
        if (!cancelled) {
          setAgents([]);
          setTrunks([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [props]);

  function mergeUpdatedNumber(
    number: ConsoleNumber,
    updated: ApiNumber,
  ): ConsoleNumber {
    return {
      ...number,
      rawDid: updated.did,
      did: formatAustralianDisplayNumber(updated.did),
      agent: updated.agent_name ?? "—",
      state: updated.agent_name ? updated.state : "Unassigned",
      sipTrunkId: updated.sip_trunk_id,
      sipTrunkName: updated.sip_trunk_name,
      trunkAssignmentStatus: updated.trunk_assignment_status,
      lastTrunkSyncError: updated.last_trunk_sync_error,
    };
  }

  async function handleAssignment(number: ConsoleNumber, agentId: string) {
    const rawDid = number.rawDid ?? didToE164(number.did);
    if (
      agentId === "unassigned" &&
      number.agent !== "—" &&
      number.state !== "Unassigned"
    ) {
      const confirmed = window.confirm(
        `Stop new calls for ${number.did}? New calls to this DID will no longer route to ${number.agent}. Existing in-progress calls are not terminated.`,
      );
      if (!confirmed) return;
    }
    setAssignmentState(`Updating agent for ${number.did}…`);
    try {
      const updated = await assignNumberToAgent(
        rawDid,
        agentId === "unassigned" ? null : agentId,
      );
      props.onNumbersChange(
        props.numbers.map((item) =>
          item.did === number.did ? mergeUpdatedNumber(item, updated) : item,
        ),
      );
      setAssignmentState(
        agentId === "unassigned"
          ? `Stopped new calls for ${number.did}`
          : `Updated ${number.did}`,
      );
    } catch (error) {
      if (isUnauthorizedApiError(error)) {
        props.onSessionExpired();
        return;
      }
      setAssignmentState(
        error instanceof ConsoleApiError
          ? error.message
          : "Number assignment failed",
      );
    }
  }

  async function handleAddNumber(values: AddNumberValues) {
    setAddBusy(true);
    setAddError(null);
    try {
      await registerNumber({
        did: values.did,
        label: values.label,
        sipTrunkId: values.sipTrunkId,
      });
      const refreshed = await fetchDashboardData();
      props.onNumbersChange(refreshed.numbers);
      setAddOpen(false);
      setAssignmentState(`${values.did} added.`);
    } catch (error) {
      if (isUnauthorizedApiError(error)) {
        props.onSessionExpired();
        return;
      }
      setAddError(
        error instanceof ConsoleApiError
          ? error.message
          : "Could not add number",
      );
    } finally {
      setAddBusy(false);
    }
  }

  async function handleTrunkAssignment(number: ConsoleNumber, trunkId: string) {
    const rawDid = number.rawDid ?? didToE164(number.did);
    setAssignmentState(`Updating SIP trunk for ${number.did}…`);
    try {
      const currentAgent = agentValueForNumber(number, agents);
      const updated = await assignNumberToTrunk(
        rawDid,
        trunkId === "unassigned" ? null : trunkId,
        currentAgent === "unassigned" ? null : currentAgent,
      );
      props.onNumbersChange(
        props.numbers.map((item) =>
          item.did === number.did ? mergeUpdatedNumber(item, updated) : item,
        ),
      );
      setAssignmentState(
        trunkId === "unassigned"
          ? `Removed ${number.did} from SIP trunk sync`
          : `Marked ${number.did} for LiveKit trunk sync`,
      );
    } catch (error) {
      if (isUnauthorizedApiError(error)) {
        props.onSessionExpired();
        return;
      }
      setAssignmentState(
        error instanceof ConsoleApiError
          ? error.message
          : "SIP trunk assignment failed",
      );
    }
  }

  return (
    <section className="screen" aria-labelledby="numbers-heading">
      <header className="screen-header">
        <div>
          <h1 id="numbers-heading">Numbers</h1>
          <p>
            {props.numbers.length} numbers loaded for this subaccount. Assign
            each DID to the inbound agent and SIP trunk that should receive
            provider calls.
          </p>
          <p className="compact-copy">
            Changing a SIP trunk assignment marks LiveKit sync as pending. Use
            the SIP Trunks screen to preview and apply the trunk after number
            changes; verify with provider/call evidence before treating it as
            production-ready.
          </p>
          {assignmentState ? (
            <p className="compact-copy">{assignmentState}</p>
          ) : null}
        </div>
        <button
          className="primary-button"
          type="button"
          onClick={() => {
            setAddError(null);
            setAddOpen(true);
          }}
        >
          <Plus size={16} aria-hidden="true" /> Add number
        </button>
      </header>
      <div className="table-card numbers-table-card">
        <table>
          <thead>
            <tr>
              <th>Number</th>
              <th>Type</th>
              <th>Agent</th>
              <th>SIP trunk</th>
              <th>Sync</th>
              <th>Status</th>
              <th>Source</th>
              <th>Monthly</th>
              <th>Kill switch</th>
            </tr>
          </thead>
          <tbody>
            {props.numbers.map((number) => (
              <tr
                className={
                  number.state === "Propagating" ||
                  number.trunkAssignmentStatus === "pending"
                    ? "highlight-row"
                    : ""
                }
                key={number.did}
              >
                <td>
                  <div className="cell-stack">
                    <span className="mono strong-cell">{number.did}</span>
                    {number.label ? <small>{number.label}</small> : null}
                  </div>
                </td>
                <td>{number.type}</td>
                <td>
                  <label className="sr-only" htmlFor={`assign-${number.did}`}>
                    Assign {number.did}
                  </label>
                  <select
                    id={`assign-${number.did}`}
                    className="compact-select"
                    value={agentValueForNumber(number, agents)}
                    onChange={(event) =>
                      void handleAssignment(number, event.target.value)
                    }
                  >
                    <option value="unassigned">Unassigned</option>
                    {agents.map((agent) => (
                      <option value={agent.id} key={agent.id}>
                        {agent.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <label className="sr-only" htmlFor={`trunk-${number.did}`}>
                    SIP trunk for {number.did}
                  </label>
                  <select
                    id={`trunk-${number.did}`}
                    className="compact-select"
                    value={number.sipTrunkId ?? "unassigned"}
                    onChange={(event) =>
                      void handleTrunkAssignment(number, event.target.value)
                    }
                  >
                    <option value="unassigned">Not on trunk</option>
                    {trunks.map((trunk) => (
                      <option value={trunk.sipTrunkId} key={trunk.sipTrunkId}>
                        {trunk.name}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <span
                    className={`status-pill ${number.trunkAssignmentStatus ?? "unknown"}`}
                  >
                    <i />
                    {number.trunkAssignmentStatus ?? "not assigned"}
                  </span>
                  {number.lastTrunkSyncError ? (
                    <small className="compact-copy">
                      {number.lastTrunkSyncError}
                    </small>
                  ) : null}
                </td>
                <td>
                  <StatusPill status={number.state} />
                </td>
                <td>
                  <span
                    className={`status-pill ${number.source.replace("_", "-")}`}
                  >
                    <i />
                    {number.source === "live_api" ? "Live API" : "Seed demo"}
                  </span>
                </td>
                <td className="mono">{number.monthly}</td>
                <td>
                  <button
                    className="secondary-button compact-action"
                    type="button"
                    disabled={
                      number.agent === "—" || number.state === "Unassigned"
                    }
                    onClick={() => void handleAssignment(number, "unassigned")}
                  >
                    Stop new calls
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <AddNumberDialog
        open={addOpen}
        trunks={trunks}
        error={addError}
        busy={addBusy}
        onSubmit={(values) => void handleAddNumber(values)}
        onClose={() => setAddOpen(false)}
      />
    </section>
  );
}

// Enough rows to scan without scrolling the page into uselessness.
const CALLS_PAGE_SIZE = 15;

function CallsScreen(props: {
  calls: CallRow[];
  runtimeStatus: RuntimeStatusRow;
  onSessionExpired: () => void;
}) {
  const realCalls = props.calls.filter(
    (call) => call.source === "real_uat",
  ).length;
  const [selectedCallId, setSelectedCallId] = useState<string | undefined>(
    undefined,
  );
  const [callsPage, setCallsPage] = useState(1);
  const [rawCosts, setRawCosts] = useState<
    Record<string, InternalCallCost | null>
  >({});
  const selectedCall =
    props.calls.find((call) => call.id === selectedCallId) ?? props.calls[0];

  useEffect(() => {
    let cancelled = false;
    const callIds = props.calls.slice(0, 25).map((call) => call.id);
    if (callIds.length === 0)
      return () => {
        cancelled = true;
      };
    Promise.all(
      callIds.map(async (callId) => {
        try {
          return [callId, await fetchInternalCallCost(callId)] as const;
        } catch (error) {
          if (isUnauthorizedApiError(error)) props.onSessionExpired();
          return [callId, null] as const;
        }
      }),
    ).then((entries) => {
      if (!cancelled) setRawCosts(Object.fromEntries(entries));
    });
    return () => {
      cancelled = true;
    };
  }, [props]);

  // Paginate. Fifty card rows produced a 7,500px page with no way to scan or
  // compare, which is the opposite of what an operator needs from a call log.
  const pageCount = Math.max(
    1,
    Math.ceil(props.calls.length / CALLS_PAGE_SIZE),
  );
  const page = Math.min(callsPage, pageCount);
  const visibleCalls = props.calls.slice(
    (page - 1) * CALLS_PAGE_SIZE,
    page * CALLS_PAGE_SIZE,
  );

  return (
    <section className="screen" aria-labelledby="calls-heading">
      <header className="screen-header compact">
        <div>
          <h1 id="calls-heading">Calls</h1>
          <p>
            {realCalls.toLocaleString()} real calls in the last 7 days. Raw
            provider cost estimates are internal and can differ from customer
            billing.
          </p>
        </div>
      </header>
      <RealModeWarning />
      <div className="calls-layout">
        <div className="table-card">
          <table className="calls-table">
            <caption className="sr-only">Calls with raw internal costs</caption>
            <thead>
              <tr>
                <th scope="col">Started</th>
                <th scope="col">Caller</th>
                <th scope="col">DID</th>
                <th scope="col">Agent</th>
                <th scope="col">Status</th>
                <th scope="col">Duration</th>
                <th scope="col">End reason</th>
                <th scope="col" className="numeric">
                  Raw cost
                </th>
              </tr>
            </thead>
            <tbody>
              {visibleCalls.length ? (
                visibleCalls.map((call) => (
                  <tr
                    className={
                      call.id === selectedCall?.id ? "selected-row" : ""
                    }
                    key={call.id}
                    onClick={() => setSelectedCallId(call.id)}
                    aria-selected={call.id === selectedCall?.id}
                  >
                    <td>
                      <button className="row-link" type="button">
                        {call.started}
                      </button>
                    </td>
                    <td className="mono">{call.caller}</td>
                    <td className="mono">{call.did ?? "—"}</td>
                    <td>{call.agent}</td>
                    <td>
                      <StatusPill
                        status={
                          call.status ??
                          (call.endReason === "never_answered"
                            ? "failed"
                            : "completed")
                        }
                      />
                    </td>
                    <td className="mono">{call.duration}</td>
                    <td>
                      <span className={`reason ${call.endReason}`}>
                        {call.endReason}
                      </span>
                    </td>
                    <td className="mono numeric">
                      {formatRawCostCell(rawCosts, call)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={8}>
                    <EmptyState message="No calls in the last 7 days. Assign a number to an agent, then call it to see rows here." />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {props.calls.length > CALLS_PAGE_SIZE ? (
            <nav className="pagination" aria-label="Calls pages">
              <button
                className="secondary-button"
                type="button"
                disabled={page <= 1}
                onClick={() => setCallsPage(page - 1)}
              >
                Previous
              </button>
              <span className="page-status">
                {(page - 1) * CALLS_PAGE_SIZE + 1}&ndash;
                {Math.min(page * CALLS_PAGE_SIZE, props.calls.length)} of{" "}
                {props.calls.length}
              </span>
              <button
                className="secondary-button"
                type="button"
                disabled={page >= pageCount}
                onClick={() => setCallsPage(page + 1)}
              >
                Next
              </button>
            </nav>
          ) : null}
        </div>
        {selectedCall ? (
          <CallDetailPanel
            call={selectedCall}
            onSessionExpired={props.onSessionExpired}
          />
        ) : null}
      </div>
    </section>
  );
}

function formatRawCostCell(
  rawCosts: Record<string, InternalCallCost | null>,
  call: CallRow,
): string {
  const rawCost = rawCosts[call.id];
  if (rawCost)
    return formatInternalMicroMinorUnits(
      rawCost.total_amount_micro_minor_units,
      rawCost.currency,
    );
  if (call.id in rawCosts) return "No raw cost";
  return "Loading…";
}

function RealModeWarning() {
  return (
    <section
      className="real-mode-warning"
      aria-label="Production calling status"
    >
      <strong>Production calling is armed</strong>
      <span>
        Calls that are routed to Console can use live provider capacity and
        recording settings. Aatrox routing still controls whether customer
        traffic reaches Console.
      </span>
    </section>
  );
}

function TestNumberPanel(props: {
  number: ConsoleNumber;
  latestRealCall?: CallRow;
}) {
  return (
    <section
      className="test-number-panel"
      aria-labelledby="test-number-heading"
    >
      <div>
        <span className="table-label">Repeatable UAT test number</span>
        <h2 id="test-number-heading" className="mono">
          {props.number.did}
        </h2>
        <p>
          Call this DID from an allowed test phone, then inspect the Calls
          screen for room, admission, runtime revision, timeline, and final
          outcome.
        </p>
      </div>
      <div className="uat-agent-card">
        <span className="table-label">Published test agent</span>
        <strong>Live API agent summary pending</strong>
        <span>Seeded agent summaries are disabled for production cutover.</span>
        <small>Use the Agents screen for authenticated live agent data.</small>
      </div>
      {props.latestRealCall ? (
        <div className="uat-agent-card">
          <span className="table-label">Latest real call</span>
          <strong>{props.latestRealCall.endReason}</strong>
          <span className="mono">{props.latestRealCall.id}</span>
          <small>{props.latestRealCall.outcome}</small>
        </div>
      ) : null}
    </section>
  );
}

function CallDetailPanel(props: {
  call: CallRow;
  onSessionExpired: () => void;
}) {
  const { call, onSessionExpired } = props;
  const timeline = call.timeline ?? [
    {
      label: "API summary loaded",
      at: call.started,
      detail:
        "Detail timeline is not available from the current API payload yet.",
      status: "pending" as const,
    },
  ];
  const displayTimeline = compactTimeline(timeline);
  const [reviewData, setReviewData] = useState<CallReviewData | null>(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [recordingLoadingId, setRecordingLoadingId] = useState<string | null>(
    null,
  );
  const [recordingError, setRecordingError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<
    | "timeline"
    | "memory"
    | "transcript"
    | "summary"
    | "recordings"
    | "internal-cost"
  >("memory");

  useEffect(() => {
    let cancelled = false;
    Promise.resolve()
      .then(() => {
        if (cancelled) return;
        setReviewLoading(true);
        return Promise.all([
          fetchCallTranscripts(call.id),
          fetchCallSummary(call.id),
          fetchCallRecordings(call.id),
          fetchInternalCallCost(call.id),
          fetchCallMemoryImpact(call.id),
        ]);
      })
      .then((result) => {
        if (cancelled || !result) return;
        const [transcripts, summary, recordings, internalCost, memoryImpact] =
          result;
        setReviewData({
          transcripts,
          summary,
          recordings,
          internalCost,
          memoryImpact,
        });
        setReviewLoading(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        if (isUnauthorizedApiError(error)) {
          onSessionExpired();
          return;
        }
        setReviewData(null);
        setReviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [call.id, onSessionExpired]);

  const hasTranscripts = reviewData && reviewData.transcripts.length > 0;
  const hasSummary = reviewData && reviewData.summary !== null;
  const hasRecordings = reviewData && reviewData.recordings.length > 0;
  const hasInternalCost = reviewData && reviewData.internalCost !== null;
  const hasMemoryImpact = reviewData && reviewData.memoryImpact !== null;

  async function openRecording(recordingId: string) {
    setRecordingLoadingId(recordingId);
    setRecordingError(null);
    try {
      const playback = await fetchCallRecordingPlaybackUrl(
        call.id,
        recordingId,
      );
      window.open(playback.url, "_blank", "noopener,noreferrer");
    } catch (error) {
      if (isUnauthorizedApiError(error)) {
        onSessionExpired();
        return;
      }
      setRecordingError(
        error instanceof ConsoleApiError
          ? error.message
          : "Unable to create recording playback URL",
      );
    } finally {
      setRecordingLoadingId(null);
    }
  }

  return (
    <aside className="call-detail panel" aria-labelledby="call-detail-heading">
      <div className="detail-header">
        <span className="table-label">Selected call</span>
        <h2 id="call-detail-heading">Call review</h2>
        <StatusPill status={call.status ?? "completed"} />
      </div>
      <dl className="detail-grid">
        <dt>Call ID</dt>
        <dd className="mono">{call.id}</dd>
        <dt>Room</dt>
        <dd className="mono">{call.roomName ?? "Not in API payload"}</dd>
        <dt>Admission</dt>
        <dd className="mono">{call.admissionId ?? "Not in API payload"}</dd>
        <dt>Runtime</dt>
        <dd className="mono">{call.runtimeRevision ?? "Not in API payload"}</dd>
        <dt>Outcome</dt>
        <dd>{call.outcome ?? call.endReason}</dd>
      </dl>
      <div className="review-tabs">
        <button
          className={`tab-button ${activeTab === "timeline" ? "active" : ""}`}
          onClick={() => setActiveTab("timeline")}
          type="button"
        >
          Timeline
        </button>
        <button
          className={`tab-button ${activeTab === "memory" ? "active" : ""}`}
          onClick={() => setActiveTab("memory")}
          type="button"
          disabled={reviewLoading || !hasMemoryImpact}
        >
          Memory
        </button>
        <button
          className={`tab-button ${activeTab === "transcript" ? "active" : ""}`}
          onClick={() => setActiveTab("transcript")}
          type="button"
          disabled={reviewLoading || !hasTranscripts}
        >
          Transcript{" "}
          {hasTranscripts ? `(${reviewData.transcripts.length})` : ""}
        </button>
        <button
          className={`tab-button ${activeTab === "summary" ? "active" : ""}`}
          onClick={() => setActiveTab("summary")}
          type="button"
          disabled={reviewLoading || !hasSummary}
        >
          Summary
        </button>
        <button
          className={`tab-button ${activeTab === "recordings" ? "active" : ""}`}
          onClick={() => setActiveTab("recordings")}
          type="button"
          disabled={reviewLoading || !hasRecordings}
        >
          Recordings {hasRecordings ? `(${reviewData.recordings.length})` : ""}
        </button>
        <button
          className={`tab-button ${activeTab === "internal-cost" ? "active" : ""}`}
          onClick={() => setActiveTab("internal-cost")}
          type="button"
          disabled={reviewLoading || !hasInternalCost}
        >
          Internal cost
        </button>
      </div>
      {activeTab === "timeline" && (
        <div className="timeline" aria-label="Call timeline">
          {displayTimeline.map((event) => (
            <div
              className={`timeline-event ${event.status}`}
              key={`${event.at}-${event.label}-${event.detail}`}
            >
              <span className="mono">{event.at}</span>
              <div>
                <strong>{event.label}</strong>
                <p>{event.detail}</p>
              </div>
            </div>
          ))}
        </div>
      )}
      {activeTab === "memory" && (
        <div className="summary-view">
          {reviewLoading && (
            <p className="loading-state">Loading memory impact...</p>
          )}
          {!reviewLoading && !hasMemoryImpact && (
            <p className="empty-state">
              No memory diagnostics available for this call.
            </p>
          )}
          {!reviewLoading && hasMemoryImpact && reviewData.memoryImpact && (
            <>
              <dl className="detail-grid">
                <dt>Memory loaded</dt>
                <dd>
                  {reviewData.memoryImpact.memory_available ? "Yes" : "No"}
                </dd>
                <dt>Memory updated</dt>
                <dd>{reviewData.memoryImpact.memory_updated ? "Yes" : "No"}</dd>
                <dt>Contact ID</dt>
                <dd className="mono">
                  {reviewData.memoryImpact.contact_id ?? "Not on call metadata"}
                </dd>
                <dt>Revision</dt>
                <dd className="mono">
                  {reviewData.memoryImpact.revision_after
                    ? `${reviewData.memoryImpact.revision_before ?? "new"} → ${reviewData.memoryImpact.revision_after}`
                    : "No revision for this call"}
                </dd>
                <dt>Status</dt>
                <dd>{reviewData.memoryImpact.status_reason}</dd>
              </dl>
              {reviewData.memoryImpact.contact_memory && (
                <div className="summary-text">
                  <h3>Current memory</h3>
                  <p>
                    {reviewData.memoryImpact.contact_memory.agent_visible_summary ||
                      "No agent-visible memory loaded for this contact."}
                  </p>
                  <MemoryStateLists memory={reviewData.memoryImpact.contact_memory} />
                </div>
              )}
              {reviewData.memoryImpact.update_interaction && (
                <div className="summary-text">
                  <h3>Memory update from this call</h3>
                  <p>{reviewData.memoryImpact.update_interaction.summary}</p>
                  {reviewData.memoryImpact.update_interaction.next_action && (
                    <p>
                      <strong>Next action:</strong>{" "}
                      {reviewData.memoryImpact.update_interaction.next_action}
                    </p>
                  )}
                </div>
              )}
              <OpenItemChangesPanel changes={reviewData.memoryImpact.open_item_changes} />
            </>
          )}
        </div>
      )}
      {activeTab === "transcript" && (
        <div className="transcript-view">
          {reviewLoading && (
            <p className="loading-state">Loading transcript...</p>
          )}
          {!reviewLoading && !hasTranscripts && (
            <p className="empty-state">
              No transcript available for this call.
            </p>
          )}
          {!reviewLoading && hasTranscripts && (
            <div className="transcript-segments">
              {reviewData.transcripts.map((segment) => (
                <div className="transcript-segment" key={segment.transcript_id}>
                  <div className="segment-meta">
                    <span className={`speaker-badge ${segment.speaker_role}`}>
                      {segment.speaker_role}
                    </span>
                    <span className="timestamp mono">
                      {formatTimestamp(segment.start_time_ms)}
                    </span>
                    {segment.confidence && (
                      <span className="confidence">
                        {Math.round(segment.confidence * 100)}%
                      </span>
                    )}
                  </div>
                  <p className="utterance">{segment.utterance_text}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {activeTab === "summary" && (
        <div className="summary-view">
          {reviewLoading && <p className="loading-state">Loading summary...</p>}
          {!reviewLoading && !hasSummary && (
            <p className="empty-state">No summary available for this call.</p>
          )}
          {!reviewLoading && hasSummary && reviewData.summary && (
            <>
              <div className="summary-text">
                <h3>Summary</h3>
                <p>{reviewData.summary.summary_text}</p>
              </div>
              {reviewData.summary.action_items.length > 0 && (
                <div className="action-items">
                  <h3>Action Items</h3>
                  <ul>
                    {reviewData.summary.action_items.map((item, i) => (
                      <li key={i}>{String(item)}</li>
                    ))}
                  </ul>
                </div>
              )}
              {Object.keys(reviewData.summary.outcome).length > 0 && (
                <div className="outcome">
                  <h3>Outcome</h3>
                  <dl>
                    {Object.entries(reviewData.summary.outcome).map(
                      ([key, value]) => (
                        <div key={key}>
                          <dt>{key}</dt>
                          <dd>{String(value)}</dd>
                        </div>
                      ),
                    )}
                  </dl>
                </div>
              )}
              {reviewData.summary.model_provider && (
                <div className="summary-meta">
                  <small className="muted">
                    Generated by {reviewData.summary.model_provider}
                    {reviewData.summary.model_name
                      ? ` / ${reviewData.summary.model_name}`
                      : ""}
                  </small>
                </div>
              )}
            </>
          )}
        </div>
      )}
      {activeTab === "recordings" && (
        <div className="recordings-view">
          {reviewLoading && (
            <p className="loading-state">Loading recordings...</p>
          )}
          {recordingError ? (
            <div className="notice danger compact-notice">
              <strong>Playback unavailable</strong>
              <p>{recordingError}</p>
            </div>
          ) : null}
          {!reviewLoading && !hasRecordings && (
            <p className="empty-state">
              No recordings available for this call.
            </p>
          )}
          {!reviewLoading && hasRecordings && (
            <div className="recordings-list">
              {reviewData.recordings.map((recording) => (
                <div className="recording-card" key={recording.recording_id}>
                  <div className="recording-header">
                    <strong>
                      {recording.recording_type.replace("_", " ")}
                    </strong>
                    <span className="status-pill">
                      <i />
                      {recording.storage_provider}
                    </span>
                  </div>
                  <dl className="recording-details">
                    {recording.duration_ms && (
                      <>
                        <dt>Duration</dt>
                        <dd>{formatDuration(recording.duration_ms)}</dd>
                      </>
                    )}
                    {recording.format && (
                      <>
                        <dt>Format</dt>
                        <dd className="mono">{recording.format}</dd>
                      </>
                    )}
                    {recording.storage_size_bytes && (
                      <>
                        <dt>Size</dt>
                        <dd>{formatBytes(recording.storage_size_bytes)}</dd>
                      </>
                    )}
                    <dt>Encrypted</dt>
                    <dd>{recording.encrypted ? "Yes" : "No"}</dd>
                    {recording.storage_reference && (
                      <>
                        <dt>Reference</dt>
                        <dd className="mono small">
                          {recording.storage_reference}
                        </dd>
                      </>
                    )}
                  </dl>
                  <button
                    className="secondary-button compact-action recording-playback-button"
                    type="button"
                    disabled={recordingLoadingId === recording.recording_id}
                    onClick={() => void openRecording(recording.recording_id)}
                  >
                    {recordingLoadingId === recording.recording_id
                      ? "Creating playback link…"
                      : "View recording"}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {activeTab === "internal-cost" && (
        <InternalCostPanel
          internalCost={reviewData?.internalCost ?? null}
          loading={reviewLoading}
        />
      )}
    </aside>
  );
}

function compactTimeline(events: CallTimelineEvent[]): CallTimelineEvent[] {
  const output: CallTimelineEvent[] = [];
  let skippedTurnLatency = 0;
  const flushSkipped = () => {
    if (skippedTurnLatency === 0) return;
    output.push({
      label: "Turn latency markers",
      at: "—",
      detail: `${skippedTurnLatency} routine per-turn telemetry marker${skippedTurnLatency === 1 ? "" : "s"} hidden to keep the call story readable.`,
      status: "pending",
    });
    skippedTurnLatency = 0;
  };

  for (const event of events) {
    if (event.label.toLowerCase().includes("turn latency")) {
      skippedTurnLatency += 1;
      continue;
    }
    flushSkipped();
    output.push(event);
  }
  flushSkipped();
  return output;
}

function InternalCostPanel(props: {
  internalCost: InternalCallCost | null;
  loading: boolean;
}) {
  const { internalCost, loading } = props;
  if (loading) return <p className="loading-state">Loading internal cost...</p>;
  if (!internalCost) {
    return (
      <p className="empty-state">
        No internal cost estimate is available for this call.
      </p>
    );
  }
  return (
    <div className="internal-cost-view">
      <div className="notice warning internal-cost-warning">
        <strong>Internal estimate — not customer billing</strong>
        <p>{internalCost.disclaimer}</p>
      </div>
      <div className="internal-cost-summary">
        <span className="table-label">Estimated Callable cost</span>
        <strong>
          {formatInternalMicroMinorUnits(
            internalCost.total_amount_micro_minor_units,
            internalCost.currency,
          )}
        </strong>
        <StatusPill
          status={
            internalCost.pricing_status === "priced" ? "completed" : "admitted"
          }
        />
      </div>
      <div className="internal-cost-lines" aria-label="Provider cost breakdown">
        {providerBreakdown(internalCost).map((provider) => (
          <div className="internal-cost-line" key={provider.key}>
            <div>
              <strong>{provider.label}</strong>
              <span>
                {provider.lineCount} cost line
                {provider.lineCount === 1 ? "" : "s"}
              </span>
            </div>
            <div className="internal-cost-line-usage">
              <span className="mono">provider total</span>
              <strong className="mono">
                {formatInternalMicroMinorUnits(
                  provider.amountMicroMinorUnits,
                  internalCost.currency,
                )}
              </strong>
            </div>
          </div>
        ))}
      </div>
      {internalCost.unpriced_line_count > 0 && (
        <p className="compact-copy">
          {internalCost.unpriced_line_count} usage line
          {internalCost.unpriced_line_count === 1 ? "" : "s"} still need
          pricing.
        </p>
      )}
      <div className="internal-cost-lines">
        {internalCost.lines.map((line) => (
          <div className="internal-cost-line" key={line.raw_cost_id}>
            <div>
              <strong>
                {formatInternalCostLabel(line.cost_component, line.usage_unit)}
              </strong>
              <span>
                {line.provider_name ?? "unknown"}
                {line.provider_model ? ` / ${line.provider_model}` : ""}
              </span>
            </div>
            <div className="internal-cost-line-usage">
              <span className="mono">
                {line.usage_quantity.toLocaleString()} {line.usage_unit}
              </span>
              <strong className="mono">
                {formatInternalMicroMinorUnits(
                  line.amount_micro_minor_units,
                  line.currency,
                )}
              </strong>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function formatInternalMicroMinorUnits(
  amountMicroMinorUnits: number,
  currency: string,
): string {
  const amount = (amountMicroMinorUnits / 100_000_000).toFixed(8);
  if (currency === "AUD") return `A$${amount}`;
  return `${currency} ${amount}`;
}

function providerBreakdown(internalCost: InternalCallCost): Array<{
  key: string;
  label: string;
  amountMicroMinorUnits: number;
  lineCount: number;
}> {
  const grouped = new Map<
    string,
    {
      key: string;
      label: string;
      amountMicroMinorUnits: number;
      lineCount: number;
    }
  >();
  internalCost.lines.forEach((line) => {
    const key = line.provider_name ?? "unknown";
    const existing = grouped.get(key) ?? {
      key,
      label: key,
      amountMicroMinorUnits: 0,
      lineCount: 0,
    };
    existing.amountMicroMinorUnits += line.amount_micro_minor_units;
    existing.lineCount += 1;
    grouped.set(key, existing);
  });
  return Array.from(grouped.values()).sort(
    (a, b) => b.amountMicroMinorUnits - a.amountMicroMinorUnits,
  );
}

function formatInternalCostLabel(component: string, usageUnit: string): string {
  const componentLabel = component.toUpperCase();
  return `${componentLabel} ${usageUnit.replaceAll("_", " ")}`;
}

function formatTimestamp(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

function formatDuration(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function VoicesScreen(props: { onSessionExpired: () => void }) {
  const { onSessionExpired } = props;
  const [voices, setVoices] = useState<ApiTtsVoice[]>([]);
  const [provider, setProvider] =
    useState<ApiTtsVoice["provider"]>("elevenlabs");
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState<ApiTtsVoice["provider"] | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadVoices = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setVoices(await fetchTtsVoices());
    } catch (err) {
      if (isUnauthorizedApiError(err)) {
        onSessionExpired();
        return;
      }
      setError(
        err instanceof ConsoleApiError ? err.message : "Could not load voices",
      );
    } finally {
      setLoading(false);
    }
  }, [onSessionExpired]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => void loadVoices(), 0);
    return () => window.clearTimeout(timeoutId);
  }, [loadVoices]);

  async function handleSync(selectedProvider: ApiTtsVoice["provider"]) {
    try {
      setSyncing(selectedProvider);
      setError(null);
      const result = await syncTtsVoices(selectedProvider);
      setVoices((current) =>
        [
          ...current.filter((voice) => voice.provider !== selectedProvider),
          ...result.voices,
        ].sort((left, right) =>
          `${left.provider}:${left.display_name}`.localeCompare(
            `${right.provider}:${right.display_name}`,
          ),
        ),
      );
      setMessage(
        `Synced ${result.synced_count} ${selectedProvider} voice(s); archived ${result.archived_count}.`,
      );
    } catch (err) {
      if (isUnauthorizedApiError(err)) {
        onSessionExpired();
        return;
      }
      setError(
        err instanceof ConsoleApiError ? err.message : "Voice sync failed",
      );
    } finally {
      setSyncing(null);
    }
  }

  const visibleVoices = voices.filter((voice) => voice.provider === provider);

  return (
    <section className="screen" aria-labelledby="voices-heading">
      <header className="screen-header">
        <div>
          <h1 id="voices-heading">Voices</h1>
          <p>
            Tenant TTS voice catalog synced from provider APIs. Agent Specs save
            provider voice IDs; this screen gives operators friendly names and
            sync status without exposing provider credentials.
          </p>
          {message ? (
            <p className="compact-copy" role="status">
              {message}
            </p>
          ) : null}
        </div>
        <div className="button-row">
          <label className="auth-field">
            <span>Provider</span>
            <select
              value={provider}
              onChange={(event) =>
                setProvider(event.target.value as ApiTtsVoice["provider"])
              }
            >
              <option value="elevenlabs">ElevenLabs</option>
              <option value="cartesia">Cartesia</option>
            </select>
          </label>
          <button
            className="primary-button"
            type="button"
            disabled={syncing !== null}
            onClick={() => void handleSync(provider)}
          >
            {syncing === provider ? "Syncing…" : `Sync ${provider} voices`}
          </button>
          <button
            className="secondary-button"
            type="button"
            disabled={loading}
            onClick={() => void loadVoices()}
          >
            Refresh
          </button>
        </div>
      </header>
      {error ? (
        <div className="notice danger">
          <strong>Error</strong>
          <p>{error}</p>
        </div>
      ) : null}
      <div className="table-card">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Provider voice ID</th>
              <th>Type</th>
              <th>Status</th>
              <th>Models</th>
              <th>Last synced</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6}>Loading voices…</td>
              </tr>
            ) : visibleVoices.length === 0 ? (
              <tr>
                <td colSpan={6}>
                  <EmptyState message="No voices synced for this provider yet. Add an active TTS credential, then sync voices." />
                </td>
              </tr>
            ) : (
              visibleVoices.map((voice) => (
                <tr key={voice.voice_id}>
                  <td className="strong-cell">{voice.display_name}</td>
                  <td className="mono">{voice.provider_voice_id}</td>
                  <td>{voice.voice_type}</td>
                  <td>
                    <span
                      className={`status-pill ${voice.status === "available" ? "synced" : "unknown"}`}
                    >
                      <i />
                      {voice.status}
                    </span>
                  </td>
                  <td>{voice.supported_models.join(", ") || "—"}</td>
                  <td>
                    {voice.last_synced_at
                      ? new Date(voice.last_synced_at).toLocaleString("en-AU")
                      : "Never"}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function CredentialsScreen(props: { onSessionExpired: () => void }) {
  const { onSessionExpired } = props;
  const [credentials, setCredentials] = useState<ApiCredential[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const loadCredentials = useCallback(async () => {
    try {
      setLoading(true);
      setStatusMessage(null);
      setCredentials(await fetchCredentials());
    } catch (error) {
      if (isUnauthorizedApiError(error)) {
        onSessionExpired();
        return;
      }
      setStatusMessage(
        error instanceof ConsoleApiError
          ? error.message
          : "Failed to load credentials",
      );
    } finally {
      setLoading(false);
    }
  }, [onSessionExpired]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      void loadCredentials();
    }, 0);
    return () => window.clearTimeout(handle);
  }, [loadCredentials]);

  async function revoke(item: ApiCredential) {
    if (item.status === "revoked") return;
    try {
      const updated = await revokeCredential(item.id);
      setCredentials((current) =>
        current.map((credential) =>
          credential.id === item.id ? updated : credential,
        ),
      );
      setStatusMessage(
        `${item.display_name} revoked. New calls and MCP resolutions must fail closed for this credential.`,
      );
    } catch (error) {
      if (isUnauthorizedApiError(error)) {
        onSessionExpired();
        return;
      }
      setStatusMessage(
        error instanceof ConsoleApiError
          ? error.message
          : "Credential revoke failed",
      );
    }
  }

  return (
    <section className="screen" aria-labelledby="credentials-heading">
      <header className="screen-header">
        <div>
          <h1 id="credentials-heading">Credentials</h1>
          <p>
            Live tenant credential metadata only. Revoke is the production kill
            switch for provider keys and MCP bearer references; it never exposes
            or deletes secret material.
          </p>
          {statusMessage ? (
            <p className="compact-copy" role="status">
              {statusMessage}
            </p>
          ) : null}
        </div>
        <button className="primary-button" type="button">
          <Plus size={16} aria-hidden="true" /> Add credential
        </button>
      </header>
      <div className="banner">
        <KeyRound size={15} aria-hidden="true" />
        <span>
          Your keys, your bill. Callable never falls back to its own provider
          accounts — a missing or revoked credential fails the call loudly
          rather than quietly spending our money.
        </span>
      </div>
      <div className="table-card">
        <table>
          <thead>
            <tr>
              <th>Provider</th>
              <th>Purpose</th>
              <th>Display name</th>
              <th>Key reference</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6}>Loading live credential rows…</td>
              </tr>
            ) : credentials.length === 0 ? (
              <tr>
                <td colSpan={6}>
                  <EmptyState message="No live credential rows loaded. Seeded masked credentials are disabled for production cutover." />
                </td>
              </tr>
            ) : (
              credentials.map((credential) => (
                <tr key={credential.id}>
                  <td>{credential.provider}</td>
                  <td>{credential.purpose}</td>
                  <td>{credential.display_name}</td>
                  <td className="mono">
                    {credential.masked_secret ?? `credential:${credential.id}`}
                  </td>
                  <td className={`status-text ${credential.status}`}>
                    {credential.status}
                  </td>
                  <td>
                    <button
                      className="secondary-button compact-action"
                      type="button"
                      disabled={credential.status === "revoked"}
                      onClick={() => void revoke(credential)}
                    >
                      {credential.status === "revoked" ? "Revoked" : "Revoke"}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function BillingScreen(props: {
  billingSummary: BillingSummaryRow;
  onSessionExpired: () => void;
}) {
  const { billingSummary, onSessionExpired } = props;
  const [history, setHistory] = useState<ApiBillingReconciliationHistory[]>([]);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [reconciliationError, setReconciliationError] = useState<string | null>(
    null,
  );

  const loadHistory = useCallback(async () => {
    try {
      setReconciliationError(null);
      setHistory(await fetchBillingReconciliations());
    } catch (err) {
      if (isUnauthorizedApiError(err)) {
        onSessionExpired();
        return;
      }
      setReconciliationError(
        err instanceof ConsoleApiError
          ? err.message
          : "Failed to load reconciliation history",
      );
    }
  }, [onSessionExpired]);

  async function runEmptyReconciliation() {
    try {
      const report = await createBillingReconciliation();
      setStatusMessage(
        `Saved reconciliation ${report.reconciliation_id ?? "without id"} with ${report.mismatch_count} mismatches.`,
      );
      await loadHistory();
    } catch (err) {
      if (isUnauthorizedApiError(err)) onSessionExpired();
      else
        setStatusMessage(
          err instanceof ConsoleApiError
            ? err.message
            : "Failed to run reconciliation",
        );
    }
  }

  async function queueLowBalanceEvent() {
    try {
      const result = await enqueueLowBalanceEvent();
      setStatusMessage(
        `Queued ${result.queued_delivery_count} low-balance webhook deliveries.`,
      );
    } catch (err) {
      if (isUnauthorizedApiError(err)) onSessionExpired();
      else
        setStatusMessage(
          err instanceof ConsoleApiError
            ? err.message
            : "Failed to queue low-balance event",
        );
    }
  }

  return (
    <section className="screen" aria-labelledby="billing-heading">
      <header className="screen-header compact">
        <div>
          <h1 id="billing-heading">Usage & billing</h1>
          <p>
            Usage visibility is live from Console rows. Customer auto-billing,
            payment-provider charges, and top-ups remain disabled until
            commercial billing is explicitly enabled.
          </p>
          {statusMessage ? (
            <p className="compact-copy" role="status">
              {statusMessage}
            </p>
          ) : null}
        </div>
      </header>
      {billingSummary.isLowBalance ? (
        <div className="notice warning">
          <strong>Low balance</strong>
          <p>
            Available balance is below {billingSummary.lowBalanceThreshold}. New
            policy-controlled calls may be denied before provider spend.
          </p>
        </div>
      ) : null}
      <div className="metric-grid billing-metrics">
        <MetricCard
          label="Available"
          value={billingSummary.available}
          detail="usable for new call holds"
          tone={billingSummary.isLowBalance ? "warn" : "good"}
        />
        <MetricCard
          label="Held"
          value={billingSummary.held}
          detail="reserved for active calls"
        />
        <MetricCard
          label="Balance"
          value={billingSummary.balance}
          detail={`wallet · ${billingSummary.currency}`}
        />
        <MetricCard
          label="Low-balance threshold"
          value={billingSummary.lowBalanceThreshold}
          detail={`updated ${billingSummary.updatedAt}`}
        />
      </div>
      <div className="billing-grid">
        <section className="panel" aria-labelledby="recent-ledger-heading">
          <h2 id="recent-ledger-heading">Recent ledger</h2>
          <div className="activity-list compact-activity">
            {billingSummary.recentLedgerEntries.length === 0 ? (
              <p>No billing ledger entries yet.</p>
            ) : (
              billingSummary.recentLedgerEntries.map((entry) => (
                <div className="activity-row billing-row" key={entry.id}>
                  <span className="muted mono">{entry.createdAt}</span>
                  <span>{entry.entryType}</span>
                  <span>{entry.description ?? "—"}</span>
                  <span className="mono strong-cell">{entry.amount}</span>
                </div>
              ))
            )}
          </div>
        </section>
        <section className="panel" aria-labelledby="recent-costs-heading">
          <h2 id="recent-costs-heading">Recent call costs</h2>
          <div className="table-card embedded-table billing-cost-table">
            <table>
              <thead>
                <tr>
                  <th>Started</th>
                  <th>Call</th>
                  <th>Agent</th>
                  <th>Duration</th>
                  <th>Cost</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {billingSummary.recentCallCosts.length === 0 ? (
                  <tr>
                    <td colSpan={6}>No charged calls yet.</td>
                  </tr>
                ) : (
                  billingSummary.recentCallCosts.map((cost) => (
                    <tr key={cost.callId}>
                      <td>{cost.startedAt ?? "—"}</td>
                      <td className="mono">{cost.toNumber ?? cost.callId}</td>
                      <td>{cost.agentName ?? "—"}</td>
                      <td className="mono">{cost.duration}</td>
                      <td className="mono strong-cell">{cost.cost}</td>
                      <td>
                        <StatusPill
                          status={cost.status as NonNullable<CallRow["status"]>}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
      <section className="panel" aria-labelledby="reconciliation-heading">
        <div className="panel-title-row">
          <h2 id="reconciliation-heading">Reconciliation history</h2>
          <div>
            <button
              className="secondary-button compact-action"
              type="button"
              onClick={() => void loadHistory()}
            >
              Refresh history
            </button>
            <button
              className="secondary-button compact-action"
              type="button"
              onClick={() => void runEmptyReconciliation()}
            >
              Save empty reconciliation
            </button>
            <button
              className="secondary-button compact-action"
              type="button"
              onClick={() => void queueLowBalanceEvent()}
            >
              Queue low-balance event
            </button>
          </div>
        </div>
        {reconciliationError ? (
          <div className="notice warning compact-notice">
            <strong>Reconciliation unavailable</strong>
            <p>{reconciliationError}</p>
          </div>
        ) : null}
        <div className="table-card embedded-table billing-cost-table">
          <table>
            <thead>
              <tr>
                <th>Created</th>
                <th>ID</th>
                <th>Matched</th>
                <th>Mismatches</th>
                <th>Console total</th>
                <th>Provider total</th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 ? (
                <tr>
                  <td colSpan={6}>No reconciliation reports yet.</td>
                </tr>
              ) : (
                history.map((row) => (
                  <tr key={row.reconciliation_id}>
                    <td>{new Date(row.created_at).toLocaleString("en-AU")}</td>
                    <td className="mono">{row.reconciliation_id}</td>
                    <td>{row.matched_call_count}</td>
                    <td>{row.mismatch_count}</td>
                    <td className="mono">{row.console_total_cents}</td>
                    <td className="mono">{row.provider_total_cents}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>
    </section>
  );
}
type WebhookPreviewEvent =
  | "call.started"
  | "call.ended"
  | "call.failed"
  | "call.report_ready"
  | "transcript.ready"
  | "summary.ready";
type WebhookPayloadProfileName = "minimal" | "standard" | "full" | "custom";

function ToolsMcpScreen(props: {
  onSessionExpired: () => void;
  onOpenAgents: () => void;
}) {
  const [tools, setTools] = useState<ApiConsoleTool[]>([]);
  const [showSetup, setShowSetup] = useState(false);
  const [setupName, setSetupName] = useState("");
  const [setupSlug, setSetupSlug] = useState("");
  const [setupKind, setSetupKind] = useState<ApiConsoleTool["kind"]>("mcp");
  const [setupUrl, setSetupUrl] = useState("");
  const [setupCredentialId, setSetupCredentialId] = useState("");
  const [credentials, setCredentials] = useState<ApiCredential[]>([]);
  const [setupMessage, setSetupMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([fetchTools(), fetchCredentials()])
      .then(([loadedTools, loadedCredentials]) => {
        if (!cancelled) {
          const activeMcpCredentials = loadedCredentials.filter(
            (credential) =>
              credential.status === "active" && credential.purpose === "mcp",
          );
          setTools(loadedTools);
          setCredentials(activeMcpCredentials);
          setSetupCredentialId(activeMcpCredentials[0]?.id ?? "");
          setError(null);
        }
      })
      .catch((err) => {
        if (isUnauthorizedApiError(err)) {
          props.onSessionExpired();
          return;
        }
        if (!cancelled)
          setError(
            err instanceof ConsoleApiError
              ? err.message
              : "Failed to load tool records",
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [props]);

  const mcpTools = tools.filter((tool) => tool.kind === "mcp");
  async function setupTool(event: FormEvent) {
    event.preventDefault();
    setSetupMessage(null);
    try {
      const selectedCredential = credentials.find(
        (credential) => credential.id === setupCredentialId,
      );
      if (!selectedCredential)
        throw new Error(
          "No active tenant MCP credential reference is available. Add a credential before setting up an MCP tool.",
        );
      const tool = await createTool({
        kind: setupKind,
        slug: setupSlug.trim(),
        display_name: setupName.trim(),
      });
      await createToolRevision(tool.tool_id, {
        schema_version: "callable.tool.v1",
        kind: setupKind,
        name: setupName.trim(),
        description: null,
        config: {
          server: {
            url: setupUrl.trim(),
            credential_ref: `credential:${selectedCredential.id}`,
          },
        },
      });
      setTools((current) => [...current, tool]);
      setSetupMessage(
        `Created ${tool.display_name}. Attach it from a saved agent version.`,
      );
      setShowSetup(false);
      setSetupName("");
      setSetupSlug("");
      setSetupUrl("");
      setSetupCredentialId(credentials[0]?.id ?? "");
    } catch (err) {
      setSetupMessage(err instanceof Error ? err.message : "Tool setup failed");
    }
  }

  return (
    <section className="screen" aria-labelledby="tools-mcp-heading">
      <header className="screen-header">
        <div>
          <h1 id="tools-mcp-heading">Tools & MCP</h1>
          <p>
            Manage reusable tools and approve MCP permissions from Agent
            Builder.
          </p>
        </div>
        <div className="button-row">
          <button
            className="secondary-button"
            type="button"
            onClick={() => setShowSetup((current) => !current)}
          >
            <Plus size={16} aria-hidden="true" />{" "}
            {showSetup ? "Close setup" : "Setup new tool"}
          </button>
          <button
            className="primary-button"
            type="button"
            onClick={props.onOpenAgents}
          >
            Open Agent Builder
          </button>
        </div>
      </header>
      {error ? (
        <div className="notice danger">
          <strong>Error</strong>
          <p>{error}</p>
        </div>
      ) : null}
      {setupMessage ? (
        <div className="notice success" role="status">
          <strong>Tool setup</strong>
          <p>{setupMessage}</p>
        </div>
      ) : null}
      {showSetup ? (
        <form
          className="panel setup-form"
          onSubmit={(event) => void setupTool(event)}
        >
          <div className="panel-title-row">
            <h2>Set up a reusable tool</h2>
            <span className="table-label">Tenant scoped</span>
          </div>
          <div className="form-grid two-columns">
            <label className="field-label">
              Display name
              <input
                required
                value={setupName}
                onChange={(event) => setSetupName(event.target.value)}
              />
            </label>
            <label className="field-label">
              Slug
              <input
                required
                pattern="[a-z][a-z0-9_]{1,79}"
                value={setupSlug}
                onChange={(event) => setSetupSlug(event.target.value)}
              />
            </label>
            <label className="field-label">
              Kind
              <select
                value={setupKind}
                onChange={(event) =>
                  setSetupKind(event.target.value as ApiConsoleTool["kind"])
                }
              >
                <option value="mcp">MCP connector</option>
              </select>
            </label>
            <label className="field-label">
              MCP server URL
              <input
                required
                type="url"
                pattern="https://.*"
                placeholder="https://mcp.example.com"
                value={setupUrl}
                onChange={(event) => setSetupUrl(event.target.value)}
              />
            </label>
            <label className="field-label">
              Tenant credential reference
              <select
                required
                value={setupCredentialId}
                onChange={(event) => setSetupCredentialId(event.target.value)}
              >
                <option value="">Select an active MCP credential</option>
                {credentials.map((credential) => (
                  <option value={credential.id} key={credential.id}>
                    {credential.display_name} · credential:{credential.id}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <p className="compact-copy">
            Only a vault reference is stored in the ToolRevisionSpec. Plaintext
            bearer tokens are never accepted or displayed.
          </p>
          {credentials.length === 0 ? (
            <p className="notice warning compact-notice">
              No active tenant MCP credential catalog is available. Add a
              credential before creating this tool.
            </p>
          ) : null}
          <button
            className="primary-button"
            disabled={!setupCredentialId || credentials.length === 0}
            type="submit"
          >
            Create tool
          </button>
        </form>
      ) : null}
      <div className="tools-mcp-grid">
        <section className="table-card">
          <div className="panel-title-row">
            <h2>Tool records</h2>
            <span className="table-label">{tools.length} live rows</span>
          </div>
          {loading ? (
            <p>Loading live tool records…</p>
          ) : tools.length === 0 ? (
            <EmptyState message="No live tool records returned by the API yet." />
          ) : (
            <div className="tool-record-list">
              {tools.map((tool) => (
                <article className="tool-record-card" key={tool.tool_id}>
                  <div>
                    <strong>{tool.display_name}</strong>
                    <p>{tool.description ?? "No description"}</p>
                  </div>
                  <dl>
                    <div>
                      <dt>Kind</dt>
                      <dd>{tool.kind}</dd>
                    </div>
                    <div>
                      <dt>Status</dt>
                      <dd>
                        <span className={`status-text ${tool.status}`}>
                          {tool.status}
                        </span>
                      </dd>
                    </div>
                    <div>
                      <dt>Slug</dt>
                      <dd className="mono">{tool.slug}</dd>
                    </div>
                  </dl>
                  <button
                    className="secondary-button compact-action"
                    type="button"
                    onClick={props.onOpenAgents}
                  >
                    Select for agent attachment
                  </button>
                </article>
              ))}
            </div>
          )}
        </section>
        <section className="table-card">
          <div className="panel-title-row">
            <h2>MCP approval path</h2>
            <span className="table-label">Agent-version scoped</span>
          </div>
          <p>
            Approvals live on a saved agent version. Open{" "}
            <strong>Agents</strong>, select a saved version, then review the MCP
            tool allowlist.
          </p>
          <p className="compact-copy">
            If no approval panel appears, attach an MCP connector to that agent
            version first.
          </p>
          <div className="panel-actions">
            <button
              className="secondary-button compact-action"
              type="button"
              onClick={props.onOpenAgents}
            >
              Review agent MCP permissions
            </button>
          </div>
          <div className="metric-grid compact-metrics">
            <MetricCard
              label="MCP connectors"
              value={mcpTools.length.toLocaleString()}
              detail="live tool rows"
            />
            <MetricCard
              label="Permission scope"
              value="Version"
              detail="runtime allowlist"
            />
          </div>
        </section>
      </div>
    </section>
  );
}

const webhookPreviewEvents: WebhookPreviewEvent[] = [
  "call.started",
  "call.ended",
  "call.failed",
  "call.report_ready",
  "transcript.ready",
  "summary.ready",
];
const webhookPayloadProfiles: WebhookPayloadProfileName[] = [
  "minimal",
  "standard",
  "full",
  "custom",
];

function buildWebhookPreview(
  eventType: WebhookPreviewEvent,
  profile: WebhookPayloadProfileName,
): string {
  const payload = {
    event_type: eventType,
    event_id:
      eventType === "call.report_ready"
        ? "call.report_ready:call_01K4EXAMPLE:v1"
        : "evt_01K4EXAMPLE",
    occurred_at: "2026-09-13T07:45:00Z",
    profile,
    call: {
      id: "call_01K4EXAMPLE",
      status: "completed",
      direction: "outbound",
      duration_seconds: 108,
      end_reason: "room_closed",
      agent: {
        id: "agent_01K4",
        version_id: "version_01K4",
        spec_hash: "sha256...",
      },
    },
    artifacts:
      eventType === "call.report_ready"
        ? {
            summary: {
              status: "known",
              data: { text: "Caller qualified and requested a callback." },
            },
            native_tools: {
              status: "known",
              items: [
                { name: "capture_fields", status: "completed" },
                { name: "disposition_call", status: "completed" },
              ],
            },
            transcript: {
              status: "final",
              reference_uri: "/v0/calls/call_01K4EXAMPLE/transcripts",
              inline_text:
                profile === "full"
                  ? "user: Hi\nagent: Thanks for calling"
                  : undefined,
            },
            recordings: {
              status: "unavailable",
              unavailable_reason: "recording_disabled",
              items: [],
            },
            latency: { status: profile === "full" ? "known" : "unknown" },
            usage: {
              status: "known",
              data: { currency: "AUD", total_cost_cents: 246, final: true },
            },
          }
        : { call_id: "call_01K4EXAMPLE" },
  };
  return JSON.stringify(payload, null, 2);
}

function WebhooksScreen(props: { onSessionExpired: () => void }) {
  const [endpoints, setEndpoints] = useState<ApiWebhookEndpoint[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [createUrl, setCreateUrl] = useState("");
  const [createSecret, setCreateSecret] = useState("");
  const [createEnabled, setCreateEnabled] = useState(true);
  const [createEvents, setCreateEvents] = useState<string[]>([
    "call.report_ready",
  ]);
  const [createMessage, setCreateMessage] = useState<string | null>(null);
  const [deliveries, setDeliveries] = useState<ApiWebhookDelivery[]>([]);
  const [selectedEvent, setSelectedEvent] =
    useState<WebhookPreviewEvent>("call.report_ready");
  const [payloadProfile, setPayloadProfile] =
    useState<WebhookPayloadProfileName>("standard");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const [loadedEndpoints, loadedDeliveries] = await Promise.all([
          fetchWebhookEndpoints(),
          fetchWebhookDeliveries(),
        ]);
        if (!cancelled) {
          setEndpoints(loadedEndpoints);
          setDeliveries(loadedDeliveries);
        }
      } catch (err) {
        if (isUnauthorizedApiError(err)) {
          props.onSessionExpired();
          return;
        }
        if (!cancelled)
          setError(
            err instanceof ConsoleApiError
              ? err.message
              : "Failed to load webhooks",
          );
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [props]);

  async function createEndpoint(event: FormEvent) {
    event.preventDefault();
    setCreateMessage(null);
    try {
      const endpoint = await createWebhookEndpoint({
        url: createUrl.trim(),
        events: createEvents,
        signingSecret: createSecret,
        enabled: createEnabled,
      });
      setEndpoints((current) => [endpoint, ...current]);
      setCreateMessage(
        `Created endpoint ${endpoint.secret_prefix}. The signing secret is stored in the vault and will not be shown again.`,
      );
      setShowCreate(false);
      setCreateUrl("");
      setCreateSecret("");
    } catch (err) {
      setCreateMessage(
        err instanceof ConsoleApiError
          ? err.message
          : "Webhook creation failed",
      );
    }
  }

  return (
    <section className="screen" aria-labelledby="webhooks-heading">
      <header className="screen-header">
        <div>
          <h1 id="webhooks-heading">Webhooks</h1>
          <p>
            Tenant endpoints, vault-backed secret references, and recent
            delivery outbox state.
          </p>
        </div>
        <button
          className="primary-button"
          type="button"
          onClick={() => setShowCreate((current) => !current)}
        >
          <Plus size={16} aria-hidden="true" />{" "}
          {showCreate ? "Close" : "Add endpoint"}
        </button>
      </header>
      {error ? (
        <div className="notice danger">
          <strong>Error</strong>
          <p>{error}</p>
        </div>
      ) : null}
      {createMessage ? (
        <div className="notice success" role="status">
          <strong>Endpoint</strong>
          <p>{createMessage}</p>
        </div>
      ) : null}
      {showCreate ? (
        <form
          className="panel setup-form"
          onSubmit={(event) => void createEndpoint(event)}
        >
          <div className="panel-title-row">
            <h2>Add webhook destination</h2>
            <span className="table-label">Secrets never rendered</span>
          </div>
          <label className="field-label">
            HTTPS URL
            <input
              required
              type="url"
              pattern="https://.*"
              placeholder="https://api.example.com/hooks"
              value={createUrl}
              onChange={(event) => setCreateUrl(event.target.value)}
            />
          </label>
          <label className="field-label">
            Signing secret
            <input
              required
              minLength={32}
              maxLength={256}
              type="password"
              autoComplete="new-password"
              value={createSecret}
              onChange={(event) => setCreateSecret(event.target.value)}
            />
            <small>
              Minimum 32 characters. Stored in the tenant vault; it is not
              returned after creation.
            </small>
          </label>
          <fieldset className="event-checkboxes">
            <legend>Events</legend>
            {webhookPreviewEvents.map((eventName) => (
              <label key={eventName}>
                <input
                  type="checkbox"
                  checked={createEvents.includes(eventName)}
                  onChange={(event) =>
                    setCreateEvents((current) =>
                      event.target.checked
                        ? [...current, eventName]
                        : current.filter((item) => item !== eventName),
                    )
                  }
                />{" "}
                {eventName}
              </label>
            ))}
          </fieldset>
          <label className="checkbox-field">
            <input
              type="checkbox"
              checked={createEnabled}
              onChange={(event) => setCreateEnabled(event.target.checked)}
            />{" "}
            Enabled immediately
          </label>
          <button
            className="primary-button"
            disabled={createEvents.length === 0}
            type="submit"
          >
            Create destination
          </button>
        </form>
      ) : null}
      <div className="webhooks-layout">
        <section
          className="table-card webhook-endpoints-card"
          aria-labelledby="webhook-endpoints-heading"
        >
          <div className="panel-title-row">
            <h2 id="webhook-endpoints-heading">Endpoints</h2>
            <span className="table-label">{endpoints.length} configured</span>
          </div>
          {endpoints.length === 0 ? (
            <p>No webhook endpoints configured yet.</p>
          ) : (
            <div className="webhook-endpoint-list">
              {endpoints.map((endpoint) => (
                <article className="webhook-endpoint-card" key={endpoint.id}>
                  <div>
                    <span className="table-label">Endpoint URL</span>
                    <p className="mono webhook-url">{endpoint.url}</p>
                  </div>
                  <div>
                    <span className="table-label">Events</span>
                    <p>{endpoint.events.join(", ")}</p>
                  </div>
                  <div className="webhook-endpoint-footer">
                    <span className="mono">{endpoint.secret_prefix}</span>
                    <StatusPill
                      status={endpoint.enabled ? "Live" : "Suspended"}
                    />
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
        <section
          className="table-card webhook-preview-card"
          aria-labelledby="webhook-preview-heading"
        >
          <div className="panel-title-row">
            <h2 id="webhook-preview-heading">Payload preview</h2>
            <span className="table-label">{selectedEvent}</span>
          </div>
          <div className="webhook-preview-controls">
            <label className="auth-field">
              <span>Event</span>
              <select
                className="compact-select"
                value={selectedEvent}
                onChange={(event) =>
                  setSelectedEvent(event.target.value as WebhookPreviewEvent)
                }
              >
                {webhookPreviewEvents.map((eventName) => (
                  <option value={eventName} key={eventName}>
                    {eventName}
                  </option>
                ))}
              </select>
            </label>
            <label className="auth-field">
              <span>Payload profile</span>
              <select
                className="compact-select"
                value={payloadProfile}
                onChange={(event) =>
                  setPayloadProfile(
                    event.target.value as WebhookPayloadProfileName,
                  )
                }
              >
                {webhookPayloadProfiles.map((profile) => (
                  <option value={profile} key={profile}>
                    {profile}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <p>
            Preview the JSON shape before registering endpoint subscriptions.{" "}
            <code>full</code> includes every currently policy-authorized report
            artifact.
          </p>
          <pre
            className="webhook-payload-preview mono"
            aria-label="Webhook payload preview"
          >
            {buildWebhookPreview(selectedEvent, payloadProfile)}
          </pre>
        </section>
        <section
          className="table-card webhook-delivery-card"
          aria-labelledby="webhook-delivery-heading"
        >
          <div className="panel-title-row">
            <h2 id="webhook-delivery-heading">Delivery log</h2>
            <span className="table-label">Recent</span>
          </div>
          <div className="webhook-delivery-table">
            <div className="webhook-delivery-header">
              <span>Event</span>
              <span>Status</span>
              <span>Attempts</span>
              <span>Result</span>
            </div>
            {deliveries.length === 0 ? (
              <p>No deliveries queued yet.</p>
            ) : (
              deliveries.map((delivery) => (
                <div className="webhook-delivery-row" key={delivery.id}>
                  <span>
                    <small>Event</small>
                    {delivery.event_type}
                  </span>
                  <span>
                    <small>Status</small>
                    {delivery.status}
                  </span>
                  <span className="mono">
                    <small>Attempts</small>
                    {delivery.attempts}/{delivery.max_attempts}
                  </span>
                  <span>
                    <small>Result</small>
                    {delivery.response_status ??
                      delivery.last_error ??
                      "pending"}
                  </span>
                </div>
              ))
            )}
          </div>
        </section>
      </div>
    </section>
  );
}

function SafetyControlsScreen(props: { onSessionExpired: () => void }) {
  const [policy, setPolicy] = useState<ApiAdmissionPolicy | null>(null);
  const [agents, setAgents] = useState<ApiAgent[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadSafetyState = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [nextPolicy, nextAgents] = await Promise.all([
        fetchAdmissionPolicy(),
        fetchAgents(),
      ]);
      setPolicy(nextPolicy);
      setAgents(nextAgents);
    } catch (err) {
      if (isUnauthorizedApiError(err)) {
        props.onSessionExpired();
        return;
      }
      setError(
        err instanceof Error ? err.message : "Unable to load safety controls",
      );
    } finally {
      setLoading(false);
    }
  }, [props]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      void loadSafetyState();
    }, 0);
    return () => window.clearTimeout(handle);
  }, [loadSafetyState]);

  const updatePolicy = async (
    update: Parameters<typeof updateAdmissionPolicy>[0],
    message: string,
  ) => {
    setStatusMessage(null);
    setError(null);
    try {
      const nextPolicy = await updateAdmissionPolicy(update);
      setPolicy(nextPolicy);
      setStatusMessage(message);
    } catch (err) {
      if (isUnauthorizedApiError(err)) {
        props.onSessionExpired();
        return;
      }
      setError(
        err instanceof Error
          ? err.message
          : "Unable to update admission policy",
      );
    }
  };

  const archiveAgentWithConfirmation = async (agent: ApiAgent) => {
    if (
      !window.confirm(
        `Archive agent "${agent.name}"? Existing DID routes to this agent will fail closed for new calls.`,
      )
    )
      return;
    setStatusMessage(null);
    setError(null);
    try {
      await archiveAgent(agent.id);
      setStatusMessage(`Archived agent ${agent.name}.`);
      await loadSafetyState();
    } catch (err) {
      if (isUnauthorizedApiError(err)) {
        props.onSessionExpired();
        return;
      }
      setError(err instanceof Error ? err.message : "Unable to archive agent");
    }
  };

  const archiveActiveVersionWithConfirmation = async (agent: ApiAgent) => {
    if (!agent.active_version_id) return;
    if (
      !window.confirm(
        `Archive the active version for "${agent.name}"? New calls requiring that version will fail closed.`,
      )
    )
      return;
    setStatusMessage(null);
    setError(null);
    try {
      await archiveAgentVersion(agent.id, agent.active_version_id);
      setStatusMessage(`Archived active version for ${agent.name}.`);
      await loadSafetyState();
    } catch (err) {
      if (isUnauthorizedApiError(err)) {
        props.onSessionExpired();
        return;
      }
      setError(
        err instanceof Error ? err.message : "Unable to archive agent version",
      );
    }
  };

  return (
    <section className="screen" aria-labelledby="safety-heading">
      <header className="screen-header">
        <div>
          <h1 id="safety-heading">Safety controls</h1>
          <p>
            Live operator kill switches for production cutover. These controls
            mutate real tenant policy; use them only when you intend new calls
            to fail closed.
          </p>
          {statusMessage ? (
            <p className="compact-copy" role="status">
              {statusMessage}
            </p>
          ) : null}
        </div>
        <button
          className="secondary-button"
          type="button"
          onClick={() => void loadSafetyState()}
        >
          Refresh
        </button>
      </header>
      {error ? (
        <div className="notice danger">
          <strong>Error</strong>
          <p>{error}</p>
        </div>
      ) : null}
      <div className="billing-grid">
        <div className="table-card">
          <div className="panel-title-row">
            <h2>Tenant new-call pause</h2>
            <StatusPill
              status={policy?.admission_paused ? "Suspended" : "Live"}
            />
          </div>
          {loading || !policy ? (
            <p>Loading live admission policy…</p>
          ) : (
            <>
              <p>
                When paused, runtime admission denies new calls with{" "}
                <code>operator_paused</code>. Existing calls are not terminated.
              </p>
              <p className="compact-copy">
                Last updated:{" "}
                {new Date(policy.updated_at).toLocaleString("en-AU")}
              </p>
              <div className="panel-actions">
                <button
                  className="secondary-button compact-action"
                  type="button"
                  disabled={policy.admission_paused}
                  onClick={() =>
                    void updatePolicy(
                      {
                        admission_paused: true,
                        pause_reason: "Paused from Console Safety controls",
                      },
                      "Tenant new-call admission paused.",
                    )
                  }
                >
                  Pause new calls
                </button>
                <button
                  className="secondary-button compact-action"
                  type="button"
                  disabled={!policy.admission_paused}
                  onClick={() =>
                    void updatePolicy(
                      { admission_paused: false },
                      "Tenant new-call admission resumed.",
                    )
                  }
                >
                  Resume new calls
                </button>
              </div>
              {policy.pause_reason ? (
                <p className="compact-copy">Reason: {policy.pause_reason}</p>
              ) : null}
            </>
          )}
        </div>
        <div className="table-card">
          <div className="panel-title-row">
            <h2>Provider-spend pause</h2>
            <StatusPill
              status={policy?.provider_spend_paused ? "Suspended" : "Live"}
            />
          </div>
          {loading || !policy ? (
            <p>Loading live provider-spend policy…</p>
          ) : (
            <>
              <p>
                When paused, runtime admission denies provider-backed new calls
                with <code>provider_budget_exceeded</code>.
              </p>
              <p className="compact-copy">
                Limits: max active calls {policy.max_active_calls}, max
                estimated call cost {policy.currency}{" "}
                {(policy.max_estimated_call_cost_cents / 100).toFixed(2)}
              </p>
              <div className="panel-actions">
                <button
                  className="secondary-button compact-action"
                  type="button"
                  disabled={policy.provider_spend_paused}
                  onClick={() =>
                    void updatePolicy(
                      {
                        provider_spend_paused: true,
                        provider_spend_pause_reason:
                          "Paused from Console Safety controls",
                      },
                      "Provider-spend admission paused.",
                    )
                  }
                >
                  Pause provider spend
                </button>
                <button
                  className="secondary-button compact-action"
                  type="button"
                  disabled={!policy.provider_spend_paused}
                  onClick={() =>
                    void updatePolicy(
                      { provider_spend_paused: false },
                      "Provider-spend admission resumed.",
                    )
                  }
                >
                  Resume provider spend
                </button>
              </div>
              {policy.provider_spend_pause_reason ? (
                <p className="compact-copy">
                  Reason: {policy.provider_spend_pause_reason}
                </p>
              ) : null}
            </>
          )}
        </div>
      </div>
      <div className="table-card">
        <div className="panel-title-row">
          <h2>Agent archive kill switches</h2>
          <span className="table-label">{agents.length} live rows</span>
        </div>
        <table>
          <thead>
            <tr>
              <th>Agent</th>
              <th>Status</th>
              <th>Active version</th>
              <th>Kill switches</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={4}>Loading live agents…</td>
              </tr>
            ) : agents.length === 0 ? (
              <tr>
                <td colSpan={4}>
                  <EmptyState message="No live agent rows loaded. Safety controls never use mock agents." />
                </td>
              </tr>
            ) : (
              agents.map((agent) => (
                <tr key={agent.id}>
                  <td>
                    <span className="strong-cell">{agent.name}</span>
                    <br />
                    <span className="mono compact-copy">{agent.id}</span>
                  </td>
                  <td>
                    <span className={`status-text ${agent.status}`}>
                      {agent.status}
                    </span>
                  </td>
                  <td className="mono">{agent.active_version_id ?? "—"}</td>
                  <td>
                    <div className="panel-actions">
                      <button
                        className="secondary-button compact-action"
                        type="button"
                        disabled={
                          !agent.active_version_id ||
                          agent.status === "archived"
                        }
                        onClick={() =>
                          void archiveActiveVersionWithConfirmation(agent)
                        }
                      >
                        Archive active version
                      </button>
                      <button
                        className="secondary-button compact-action"
                        type="button"
                        disabled={agent.status === "archived"}
                        onClick={() => void archiveAgentWithConfirmation(agent)}
                      >
                        Archive agent
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function RuntimeFlagsScreen(props: { onSessionExpired: () => void }) {
  const [flags, setFlags] = useState<ApiRuntimeFlag[]>([]);
  const [summary, setSummary] = useState({ total: 0, overridden: 0, invalid: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadFlags = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchRuntimeFlags();
      setFlags(response.flags);
      setSummary({
        total: response.total_count,
        overridden: response.overridden_count,
        invalid: response.invalid_count,
      });
    } catch (err) {
      if (isUnauthorizedApiError(err)) {
        props.onSessionExpired();
        return;
      }
      setError(err instanceof Error ? err.message : "Unable to load runtime flags");
    } finally {
      setLoading(false);
    }
  }, [props]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      void loadFlags();
    }, 0);
    return () => window.clearTimeout(handle);
  }, [loadFlags]);

  const groupedFlags = useMemo(() => {
    const groups = new Map<ApiRuntimeFlag["category"], ApiRuntimeFlag[]>();
    for (const flag of flags) {
      groups.set(flag.category, [...(groups.get(flag.category) ?? []), flag]);
    }
    return Array.from(groups.entries());
  }, [flags]);

  return (
    <section className="screen" aria-labelledby="runtime-flags-heading">
      <header className="screen-header">
        <div>
          <h1 id="runtime-flags-heading">Runtime flags</h1>
          <p>
            Developer-only visibility for registered rollout flags and kill switches. This
            screen never dumps arbitrary environment variables.
          </p>
        </div>
        <button className="secondary-button" type="button" onClick={() => void loadFlags()}>
          Refresh
        </button>
      </header>
      {error ? (
        <div className="notice danger">
          <strong>Error</strong>
          <p>{error}</p>
        </div>
      ) : null}
      <div className="metrics-grid">
        <MetricCard label="Registered" value={String(summary.total)} detail="Known safe flags" />
        <MetricCard label="Overridden" value={String(summary.overridden)} detail="Set in env" tone="warn" />
        <MetricCard label="Invalid" value={String(summary.invalid)} detail="Need correction" tone={summary.invalid > 0 ? "warn" : "good"} />
      </div>
      {loading ? (
        <div className="table-card"><p>Loading runtime flag registry…</p></div>
      ) : groupedFlags.length === 0 ? (
        <EmptyState message="No runtime flags registered. Add flags to the API registry before exposing them in the dashboard." />
      ) : (
        groupedFlags.map(([category, categoryFlags]) => (
          <section className="table-card" key={category}>
            <div className="panel-title-row">
              <h2>{runtimeFlagCategoryLabel(category)}</h2>
              <span className="table-label">{categoryFlags.length} flags</span>
            </div>
            <div className="webhook-delivery-table runtime-flags-table">
              <div className="webhook-delivery-header">
                <span>Flag</span>
                <span>Effective</span>
                <span>Status</span>
                <span>Purpose</span>
              </div>
              {categoryFlags.map((flag) => (
                <article className="webhook-delivery-row" key={flag.name}>
                  <span className="mono">{flag.name}</span>
                  <span className="mono">{flag.effective_value}</span>
                  <span className={`status-text ${runtimeFlagStatusClass(flag.status)}`}>
                    {flag.status}
                  </span>
                  <span>
                    <strong>{flag.description}</strong>
                    <br />
                    <span className="compact-copy">Risk: {flag.risk}</span>
                    <br />
                    <span className="compact-copy">
                      Reads: {flag.read_by.join(", ")} · deploy passthrough:{" "}
                      {flag.deploy_passthrough ? "yes" : "no"}
                    </span>
                    <br />
                    <span className="compact-copy">
                      Recommended: {flag.recommended_next_state}
                    </span>
                  </span>
                </article>
              ))}
            </div>
          </section>
        ))
      )}
    </section>
  );
}

function runtimeFlagCategoryLabel(category: ApiRuntimeFlag["category"]): string {
  switch (category) {
    case "rollout":
      return "Rollout flags";
    case "kill_switch":
      return "Kill switches";
    case "performance":
      return "Performance controls";
    case "provider_region":
      return "Provider region controls";
  }
}

function runtimeFlagStatusClass(status: ApiRuntimeFlag["status"]): string {
  if (status === "invalid") return "danger";
  if (status === "overridden") return "warning";
  return "success";
}

function SettingsScreen(props: {
  currentUser: CurrentUser | null;
  onSessionExpired: () => void;
}) {
  const [members, setMembers] = useState<ApiDashboardMember[]>([]);
  const [invitations, setInvitations] = useState<ApiDashboardInvitation[]>([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] =
    useState<ApiDashboardMember["role"]>("developer");
  const [inviteToken, setInviteToken] = useState<string | null>(null);
  const [acceptToken, setAcceptToken] = useState("");
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const loadSettings = useCallback(async () => {
    try {
      const [loadedMembers, loadedInvitations] = await Promise.all([
        fetchDashboardMembers(),
        fetchDashboardInvitations(),
      ]);
      setMembers(loadedMembers);
      setInvitations(loadedInvitations);
    } catch (err) {
      if (isUnauthorizedApiError(err)) {
        props.onSessionExpired();
        return;
      }
      setStatusMessage(
        err instanceof ConsoleApiError
          ? err.message
          : "Failed to load settings",
      );
    }
  }, [props]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      void loadSettings();
    }, 0);
    return () => window.clearTimeout(handle);
  }, [loadSettings]);

  async function inviteMember(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      const created = await createDashboardInvitation({
        email: inviteEmail,
        role: inviteRole,
        expires_in_hours: 168,
      });
      setInvitations((current) => [created.invitation, ...current]);
      setInviteEmail("");
      setInviteToken(created.invitation_token);
      setStatusMessage(
        `Created invitation for ${created.invitation.email}. Copy the token now; it will not be shown again.`,
      );
    } catch (err) {
      if (isUnauthorizedApiError(err)) props.onSessionExpired();
      else
        setStatusMessage(
          err instanceof ConsoleApiError
            ? err.message
            : "Failed to create invitation",
        );
    }
  }

  async function acceptInvite(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      await acceptDashboardInvitation(acceptToken);
      setAcceptToken("");
      setStatusMessage("Invitation accepted. Refreshing tenant context.");
      await loadSettings();
    } catch (err) {
      if (isUnauthorizedApiError(err)) props.onSessionExpired();
      else
        setStatusMessage(
          err instanceof ConsoleApiError
            ? err.message
            : "Failed to accept invitation",
        );
    }
  }

  async function changeRole(
    member: ApiDashboardMember,
    role: ApiDashboardMember["role"],
  ) {
    if (
      props.currentUser?.user_id === member.user_id &&
      role !== "owner" &&
      role !== "admin"
    ) {
      setStatusMessage("Ask another tenant admin to change your admin access.");
      return;
    }
    try {
      const updated = await updateDashboardMemberRole(
        member.membership_id,
        role,
      );
      setMembers((current) =>
        current.map((item) =>
          item.membership_id === updated.membership_id ? updated : item,
        ),
      );
      setStatusMessage(`Updated ${updated.email} to ${updated.role}`);
    } catch (err) {
      if (isUnauthorizedApiError(err)) props.onSessionExpired();
      else
        setStatusMessage(
          err instanceof ConsoleApiError
            ? err.message
            : "Failed to update role",
        );
    }
  }

  async function revoke(member: ApiDashboardMember) {
    if (
      props.currentUser?.user_id === member.user_id &&
      (member.role === "owner" || member.role === "admin")
    ) {
      setStatusMessage("Ask another tenant admin to revoke your admin access.");
      return;
    }
    try {
      await revokeDashboardMember(member.membership_id);
      setMembers((current) =>
        current.filter((item) => item.membership_id !== member.membership_id),
      );
      setStatusMessage(`Revoked ${member.email}`);
    } catch (err) {
      if (isUnauthorizedApiError(err)) props.onSessionExpired();
      else
        setStatusMessage(
          err instanceof ConsoleApiError
            ? err.message
            : "Failed to revoke member",
        );
    }
  }

  return (
    <section className="screen" aria-labelledby="settings-heading">
      <header className="screen-header">
        <div>
          <h1 id="settings-heading">Settings</h1>
          <p>Manage workspace members, invitations, and call recording.</p>
          {statusMessage ? (
            <p className="compact-copy" role="status">
              {statusMessage}
            </p>
          ) : null}
        </div>
      </header>
      <RecordingPolicyPanel onSessionExpired={props.onSessionExpired} />
      {inviteToken ? (
        <div className="notice warning">
          <strong>Invitation token</strong>
          <p className="mono">{inviteToken}</p>
          <p>
            Copy this now. Console only shows it once and stores only a hash.
          </p>
        </div>
      ) : null}
      <div className="billing-grid">
        <form
          className="table-card"
          onSubmit={(event) => void inviteMember(event)}
        >
          <div className="panel-title-row">
            <h2>Invite member</h2>
            <span className="table-label">7 day expiry</span>
          </div>
          <label className="auth-field">
            <span>Email</span>
            <input
              value={inviteEmail}
              onChange={(event) => setInviteEmail(event.target.value)}
              placeholder="ops@example.com"
            />
          </label>
          <label className="auth-field">
            <span>Role</span>
            <select
              className="compact-select"
              value={inviteRole}
              onChange={(event) =>
                setInviteRole(event.target.value as ApiDashboardMember["role"])
              }
            >
              <option value="admin">Admin</option>
              <option value="developer">Developer</option>
              <option value="billing">Billing</option>
              <option value="viewer">Viewer</option>
            </select>
          </label>
          <button className="primary-button" type="submit">
            Create invitation
          </button>
        </form>
        <form
          className="table-card"
          onSubmit={(event) => void acceptInvite(event)}
        >
          <div className="panel-title-row">
            <h2>Accept invitation</h2>
            <span className="table-label">Signed-in user</span>
          </div>
          <label className="auth-field">
            <span>Invitation token</span>
            <input
              value={acceptToken}
              onChange={(event) => setAcceptToken(event.target.value)}
              placeholder="Paste invitation token"
            />
          </label>
          <button className="secondary-button" type="submit">
            Accept invitation
          </button>
        </form>
      </div>
      <div className="table-card">
        <div className="panel-title-row">
          <h2>Invitations</h2>
          <span className="table-label">{invitations.length} recent</span>
        </div>
        <table>
          <thead>
            <tr>
              <th>Email</th>
              <th>Role</th>
              <th>Status</th>
              <th>Expires</th>
            </tr>
          </thead>
          <tbody>
            {invitations.length === 0 ? (
              <tr>
                <td colSpan={4}>No invitations yet.</td>
              </tr>
            ) : (
              invitations.map((invite) => (
                <tr key={invite.invitation_id}>
                  <td>{invite.email}</td>
                  <td>{invite.role}</td>
                  <td>
                    {invite.accepted_at
                      ? "accepted"
                      : invite.revoked_at
                        ? "revoked"
                        : "pending"}
                  </td>
                  <td>
                    {new Date(invite.expires_at).toLocaleDateString("en-AU")}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="table-card">
        <div className="panel-title-row">
          <h2>Members</h2>
          <span className="table-label">{members.length} active</span>
        </div>
        <table>
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>Role</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {members.length === 0 ? (
              <tr>
                <td colSpan={4}>No members visible.</td>
              </tr>
            ) : (
              members.map((member) => (
                <tr key={member.membership_id}>
                  <td>{member.email}</td>
                  <td>{member.full_name ?? "—"}</td>
                  <td>
                    <label
                      className="sr-only"
                      htmlFor={`role-${member.membership_id}`}
                    >
                      Role for {member.email}
                    </label>
                    <select
                      id={`role-${member.membership_id}`}
                      className="compact-select"
                      value={member.role}
                      onChange={(event) =>
                        void changeRole(
                          member,
                          event.target.value as ApiDashboardMember["role"],
                        )
                      }
                    >
                      <option value="owner">Owner</option>
                      <option value="admin">Admin</option>
                      <option value="developer">Developer</option>
                      <option value="billing">Billing</option>
                      <option value="viewer">Viewer</option>
                    </select>
                  </td>
                  <td>
                    <button
                      className="secondary-button compact-action"
                      type="button"
                      onClick={() => void revoke(member)}
                    >
                      Revoke
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function agentValueForNumber(
  number: ConsoleNumber,
  agents: ApiAgent[],
): string {
  const matched = agents.find((agent) => agent.name === number.agent);
  return matched?.id ?? "unassigned";
}

function didToE164(value: string): string {
  return value.replace(/\s/g, "");
}

function formatAustralianDisplayNumber(value: string): string {
  if (!value.startsWith("+61")) return value;
  const local = value.replace("+61", "").replace(/\s/g, "");
  if (local.startsWith("4") && local.length === 9) {
    return `+61 ${local.slice(0, 3)} ${local.slice(3, 6)} ${local.slice(6)}`;
  }
  if (local.length === 9) {
    return `+61 ${local.slice(0, 1)} ${local.slice(1, 5)} ${local.slice(5)}`;
  }
  return value;
}
